'use client';
// Dossiers de vérification / validation de méthode — gabarit TUNAC F.BM.20 rév02.
//
// L'écran central de cet outil est un ASSISTANT, et c'est une demande explicite :
// « tu nous demandes quels tests faire, on remplit les données demandées ». Il
// ne présente donc pas un formulaire de treize sections vides. Il déroule la
// grille que le gabarit impose selon le type de résultat et la portée, et pour
// chaque modalité il dit ce qui est attendu ET POURQUOI.
//
// Trois choses que cet écran refuse de faire :
//
//  · Conclure. « Déclarer apte » n'est pas un bouton d'état : c'est une
//    signature, elle exige une décision écrite et elle porte le nom de qui la
//    pose. L'outil calcule, compare, et dit ce qui manque.
//
//  · Traiter « sans verdict » comme « conforme ». Un essai sans critère
//    applicable — ou dont le rattachement au référentiel n'a pas été confirmé —
//    n'est pas conforme : il est sans verdict, et cela se voit.
//
//  · Laisser croire qu'un dossier vide est un dossier propre. Le bandeau des
//    blocages est visible en permanence et nomme chaque élément obligatoire
//    manquant.
import React, { useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { BoutonJournal } from '@/components/BoutonJournal';
import { useGed } from '@/lib/store';
import { fmtDate , equipementsDesignables } from '@/lib/meta';
import {
  I, card, inputStyle, EmptyState, RefISO, useBusy, Selecteur, BoutonIcone, useConfirmation } from '@/components/ui';
import { Tableau, ligneOuvrable, SansOuvrir, enTeteCollant } from '@/shared/Tableau';
import { SuppressionRaisonnee, type Consequence } from '@/components/SuppressionRaisonnee';
import { H1, lbl } from './ressources/commun';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
type Serie = {
  documentId: string; niveau: number; libelleNiveau: string | null;
  niveauSfbc: string | null; niveauSfbcConfirme: boolean;
  cible: number | null; sourceCible: string | null;
  operateur: string | null; lotReactif: string | null; lotCalibrant: string | null;
  dateDebut: string | null; dateFin: string | null;
  mesures: number[]; exclus: unknown[];
  n: number | null; moyenne: number | null; ecartType: number | null;
  cv: number | null; biais: number | null; seuilApplique: number | null; verdict: string;
};
type Essai = {
  documentId: string; element: number; type: string; applicable: boolean;
  /** Le paramètre visé quand le dossier en couvre plusieurs. VIDE = l'essai
   *  vaut pour TOUS — contamination, robustesse et stabilité qualifient la
   *  méthode, pas un analyte (25/08/2026). */
  parametreCode?: string | null;
  /** La matière visée. VIDE = l'essai vaut pour TOUTES — la partie analytique
   *  d'une méthode à extraction : après extraction, l'ADN ne porte plus son
   *  origine. */
  matriceCode?: string | null;
  justificationNonApplicable: string | null; typeVerification: string | null;
  sourceCritere: string | null; versionCritere: string | null; referenceCritere: string | null;
  planExperience: Record<string, unknown> | null; resultats: unknown;
  criteres: Record<string, unknown> | null; verdict: string; conclusion: string | null;
  saisiPar: string | null; dateSaisie: string | null; series: Serie[];
};
type Modalite = {
  element: number; type: string; libelle: string; section: string;
  attendu: string; pourquoi: string; exigence: string;
  familleCritere: string | null; essai: Essai | null;
};
type Entete = {
  documentId: string; ref: string; version: number;
  analyteCode: string | null; analyteLibelle: string;
  /** Les paramètres du dossier, quand il en couvre plusieurs. */
  parametres?: { code: string; libelle?: string; techniqueCode?: string | null; techniqueLibelle?: string | null; equipementCode?: string | null }[];
  presentation?: 'tableau' | 'sections';
  /** Les matières couvertes — la 4e dimension de la ligne de portée
   *  d'accréditation. Vide sur un dossier d'avant le 26/08/2026. */
  matrices?: { code: string; libelle: string }[];
  equipementCode: string | null; equipementLibelle: string | null;
  /** Second automate MIROIR — toujours UN SEUL (décision RQ du 14/08/2026).
   *  Deux appareils identiques qui rendent le même examen doivent démontrer
   *  qu'ils rendent la même chose : c'est ce que le §7.3.3 attend dès qu'un
   *  examen est porté par plus d'un instrument. */
  equipementMiroirCode?: string | null; equipementMiroirLibelle?: string | null;
  libelleExamen: string | null; domaine: string | null; sousDomaine: string | null;
  typeResultat: string; portee: string; mot: string;
  /* Repris du catalogue à la création et jamais ressaisis ici : ils décrivent
     l'examen, pas le dossier qui l'étudie. */
  sousDomaineTechnique: string | null;
  /** §3 — recalculé à chaque lecture depuis les habilitations, jamais saisi. */
  operateursHabilites: { nom: string; niveau: string }[];
  processusComplexe: boolean; nbSousProcessus: number | null; flexibilite: string | null;
  descriptionMethode: Record<string, string>; miseEnOeuvre: Record<string, string>;
  maitriseRisques: { lignes?: LigneRisque[]; conclusion?: string } | null;
  elementsSuivi: Record<string, string> | null;
  donneesInformatiques: Record<string, string> | null;
  docCode: string | null;
  /** Chaque valeur reprise garde SA PROVENANCE — et `absent` dit pourquoi une
   *  source n'a rien donné, au lieu de laisser un champ vide qu'on prendrait
   *  pour un oubli de saisie. */
  prefill: Record<string, { source: string; valeurs: Record<string, unknown>; absent?: string } | null>;
  commentaires: string | null; conclusionAptitude: string | null;
  statut: string; fige: boolean;
  refSourceFigee: string | null; refVersionFigee: string | null; dvmPrecedent: string | null;
  dateCreation: string | null; dateDecision: string | null;
};
type Blocage = { element: number | null; libelle: string; motif: string };
/* `propose` marque une ligne REPRISE DU FONDS et pas encore reprise à son
   compte : elle s'affiche, elle s'imprime nulle part, et elle ne compte pas
   comme étape analysée. `remarques` porte ce qui ne vaut que pour CETTE
   technique — la demande de la RQ du 14/08 : « on les retrouve et on peut
   rajouter des remarques qui concerneraient spécifiquement cette technique ». */
type LigneRisque = { famille: string; donneesEntree?: string; pointsCritiques?: string; criticite?: string; maitrise?: string; conclusion?: string; remarques?: string; propose?: boolean };
type Reserve = { portee: string; texte: string };
type Chapitre = { num: string; titre: string; complet: boolean; motif: string };
type Dossier = {
  dossier: Entete; grille: Modalite[];
  signatures: { documentId: string; nature: string; decision: string; signataireNom: string; dateSignature: string }[];
  referentiel: {
    documentId: string; libelle: string; unite: string | null;
    niveauBas: number | null; niveauMoyen: number | null; niveauEleve: number | null;
    sourceCode: string | null; sourceVersion: string | null; referenceBiblio: string | null;
  } | null;
  blocages: Blocage[]; avertissements: string[]; peutConclure: boolean; peutEcrire: boolean;
  reserves: Reserve[]; chapitres: Chapitre[]; chapitresIncomplets: Chapitre[];
};
/** Les neuf familles de la grille 5M du gabarit (§4), dans son ordre. Ce ne sont
 *  pas des catégories libres : le formulaire les nomme. */
const FAMILLES_5M: [string, string][] = [
  ['matiere', 'Matière (échantillon)'],
  ['main_oeuvre', 'Main d’œuvre'],
  ['milieu', 'Milieu'],
  ['materiel_reactifs', 'Matériel — réactifs'],
  ['methode', 'Méthode'],
  ['materiel_logiciel', 'Matériel — équipement et logiciel'],
  ['informatique_embarquee', 'Informatique embarquée'],
  ['transferts', 'Transferts informatiques'],
  ['post_analytique', 'Post-analytique'],
];

type LigneListe = {
  documentId: string; ref: string; version: number; analyteLibelle: string;
  equipementCode: string | null; typeResultat: string; portee: string; mot: string;
  statut: string; fige: boolean; dateCreation: string | null; dateDecision: string | null;
  dvmPrecedent: string | null; nbObligatoires: number; nbBlocages: number; nbEssaisRenseignes: number;
};
type Liste = {
  lignes: LigneListe[];
  compte: { total: number; brouillons: number; aApprouver: number; aptes: number; inaptes: number };
  peutEcrire: boolean;
};

const STATUTS: Record<string, { label: string; bg: string; fg: string }> = {
  brouillon: { label: 'Brouillon', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
  en_cours: { label: 'En cours de saisie', bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
  a_approuver: { label: 'À approuver', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  apte: { label: 'Apte', bg: 'var(--green-50)', fg: 'var(--green-600)' },
  inapte: { label: 'Inapte', bg: 'var(--red-50)', fg: 'var(--red-600)' },
  obsolete: { label: 'Obsolète — révisé', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
};
const NIVEAUX_REF: Record<string, string> = { bas: 'Bas', moyen: 'Moyen', eleve: 'Élevé' };
const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 12.5 };
/** L'exigence d'une modalité du gabarit, en toutes lettres. */
const libelleExigence = (e: string): string => (
  e === 'obligatoire_experimental' ? 'Obligatoire — étude expérimentale' : e === 'obligatoire' ? 'Obligatoire' : 'Facultatif');
/** L'état d'une modalité : renseignée (applicable ou non), ou encore à renseigner. */
const etatModalite = (m: Modalite): 'a_renseigner' | 'non_applicable' | 'applicable' => (
  !m.essai ? 'a_renseigner' : m.essai.applicable === false ? 'non_applicable' : 'applicable');
const LIBELLE_ETAT_MODALITE = { a_renseigner: 'à renseigner', non_applicable: 'Non applicable (justifié)', applicable: 'Applicable' } as const;

const pastille = (m: { label: string; bg: string; fg: string }) => (
  <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: m.bg, color: m.fg }}>{m.label}</span>
);

/** Le verdict d'un essai ou d'une série. « sans verdict » n'est ni vert ni
 *  rouge : c'est un état à part, et le confondre avec « conforme » est
 *  exactement l'erreur que cet outil existe pour empêcher. */
function Verdict({ v, seuil }: { v: string; seuil?: number | null }) {
  if (v === 'conforme') return <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--green-600)' }}>conforme{seuil !== null && seuil !== undefined ? ` (≤ ${seuil})` : ''}</span>;
  if (v === 'non_conforme') return <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red-600)' }}>non conforme{seuil !== null && seuil !== undefined ? ` (> ${seuil})` : ''}</span>;
  return (
    <span title="Aucun critère applicable n’a été retenu, ou le rattachement du niveau au référentiel n’a pas été confirmé. Ce n’est pas « conforme »."
      style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)', cursor: 'help' }}>sans verdict</span>
  );
}

/* ==========================================================================
 *  Saisie d'une série de mesures
 * ======================================================================== */

function SerieForm({ dvmId, essai, serie, referentiel, onFait, onAnnule }: {
  dvmId: string; essai: Essai; serie: Serie | null; referentiel: Dossier['referentiel'];
  onFait: () => void; onAnnule: () => void;
}) {
  const [f, setF] = useState(() => ({
    niveau: String(serie?.niveau ?? ((essai.series.length || 0) + 1)),
    libelleNiveau: serie?.libelleNiveau || '',
    cible: serie?.cible === null || serie?.cible === undefined ? '' : String(serie.cible),
    sourceCible: serie?.sourceCible || '',
    operateur: serie?.operateur || '', lotReactif: serie?.lotReactif || '', lotCalibrant: serie?.lotCalibrant || '',
    dateDebut: serie?.dateDebut || '', dateFin: serie?.dateFin || '',
    mesures: (serie?.mesures || []).join(' '),
    niveauSfbc: serie?.niveauSfbc || '',
    niveauSfbcConfirme: !!serie?.niveauSfbcConfirme,
  }));
  /* La série LUE à l'ouverture (14/10/2026) : le serveur n'écrit que ce qui a changé depuis, et
     refuse (409) des mesures modifiées entre-temps par un autre au lieu de les écraser. */
  const [base] = useState(() => (serie ? {
    niveau: serie.niveau, libelleNiveau: serie.libelleNiveau || '', cible: serie.cible ?? null, sourceCible: serie.sourceCible || '',
    operateur: serie.operateur || '', lotReactif: serie.lotReactif || '', lotCalibrant: serie.lotCalibrant || '',
    dateDebut: serie.dateDebut || '', dateFin: serie.dateFin || '', mesures: serie.mesures || [],
    niveauSfbc: serie.niveauSfbc || '', niveauSfbcConfirme: !!serie.niveauSfbcConfirme,
  } : null));
  const [erreur, setErreur] = useState('');
  const [propose, setPropose] = useState<{ niveau: string | null; ecartRelatif: number | null; ambigu: boolean } | null>(null);
  const { run, estOccupe } = useBusy();
  const maj = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));

  // La virgule n’est PAS un séparateur : le laboratoire écrit « 5,02 ».
  const nSaisi = f.mesures.split(/[\s;]+/).filter(Boolean).length;

  const enregistrer = () => run('save', async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/dvm/${dvmId}/serie`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...f, essaiId: essai.documentId, serieId: serie?.documentId || '', ...(base ? { base } : {}) }),
    });
    const j = await r.json().catch(() => null);
    if (!r.ok) { setErreur(j?.error?.message || 'Enregistrement refusé.'); return; }
    if (j?.propose) setPropose(j.propose);
    onFait();
  });

  return (
    <div style={{ ...card, padding: 14, display: 'grid', gap: 12, background: 'var(--gray-50)' }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>
        {serie ? `Niveau ${serie.niveau} — modifier les mesures` : 'Ajouter un niveau de mesures'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
        <div><label style={lbl} htmlFor="s-niv">Niveau</label>
          <input id="s-niv" style={inputStyle} value={f.niveau} onChange={(e) => maj('niveau', e.target.value)} /></div>
        <div><label style={lbl} htmlFor="s-lib">Intitulé</label>
          <input id="s-lib" style={inputStyle} value={f.libelleNiveau} onChange={(e) => maj('libelleNiveau', e.target.value)} placeholder="Contrôle niveau 1" /></div>
        <div><label style={lbl} htmlFor="s-cib">Cible assignée</label>
          <input id="s-cib" style={inputStyle} value={f.cible} onChange={(e) => maj('cible', e.target.value)} /></div>
        <div><label style={lbl} htmlFor="s-scib">Origine de la cible</label>
          <input id="s-scib" style={inputStyle} value={f.sourceCible} onChange={(e) => maj('sourceCible', e.target.value)} placeholder="fiche de lot fournisseur" /></div>
        <div><label style={lbl} htmlFor="s-op">Opérateur</label>
          <input id="s-op" style={inputStyle} value={f.operateur} onChange={(e) => maj('operateur', e.target.value)} /></div>
        <div><label style={lbl} htmlFor="s-lr">Lot de réactif</label>
          <input id="s-lr" style={inputStyle} value={f.lotReactif} onChange={(e) => maj('lotReactif', e.target.value)} /></div>
        <div><label style={lbl} htmlFor="s-d1">Du</label>
          <input id="s-d1" type="date" style={inputStyle} value={f.dateDebut} onChange={(e) => maj('dateDebut', e.target.value)} /></div>
        <div><label style={lbl} htmlFor="s-d2">Au</label>
          <input id="s-d2" type="date" style={inputStyle} value={f.dateFin} onChange={(e) => maj('dateFin', e.target.value)} /></div>
      </div>

      <div>
        <label style={lbl} htmlFor="s-mes">Mesures brutes — {nSaisi} valeur{nSaisi > 1 ? 's' : ''}</label>
        <textarea id="s-mes" rows={4} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }}
          value={f.mesures} onChange={(e) => maj('mesures', e.target.value)}
          placeholder="5,02 5,08 4,97 …  (séparées par des espaces, des retours à la ligne ou des points-virgules — pas par des virgules, qui sont vos décimales)" />
        <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Moyenne, écart-type d’échantillon <span style={mono}>(n−1)</span>, CV et biais sont calculés
          par le serveur et <strong>stockés</strong> : ils ne se recalculeront pas si la méthode de
          calcul change un jour. Une valeur non numérique est refusée plutôt qu’ignorée — l’ignorer
          changerait <span style={mono}>n</span> sans le dire.
        </p>
      </div>

      {referentiel && (
        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 10 }}>
          <span style={lbl}>Colonne du référentiel contre laquelle ce niveau est jugé</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select aria-label="Colonne du référentiel" style={{ ...inputStyle, width: 160 }} value={f.niveauSfbc}
              onChange={(e) => maj('niveauSfbc', e.target.value)}>
              <option value="">— non rattaché —</option>
              <option value="bas">Bas{referentiel.niveauBas !== null ? ` (${referentiel.niveauBas})` : ''}</option>
              <option value="moyen">Moyen{referentiel.niveauMoyen !== null ? ` (${referentiel.niveauMoyen})` : ''}</option>
              <option value="eleve">Élevé{referentiel.niveauEleve !== null ? ` (${referentiel.niveauEleve})` : ''}</option>
            </select>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: 'var(--text-body)' }}>
              <input type="checkbox" checked={f.niveauSfbcConfirme} onChange={(e) => maj('niveauSfbcConfirme', e.target.checked)} />
              Je confirme ce rattachement
            </label>
          </div>
          <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Ce rattachement <strong>détermine le seuil d’acceptation</strong>. L’outil le propose en
            comparant la cible aux trois concentrations du référentiel, mais ne l’applique
            qu’une fois confirmé : sans confirmation, la série reste sans verdict.
          </p>
          {propose?.ambigu && (
            <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.5 }}>
              La cible tombe à mi-chemin entre deux colonnes. Le choix (interpolation ou seuil le plus
              strict) appartient au laboratoire, pas au logiciel.
            </p>
          )}
        </div>
      )}

      {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: 'Enregistrer la série', libelleChargement: 'Calcul…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
    </div>
  );
}

/* ==========================================================================
 *  §5.8 — l'incertitude de mesure, rattachée et non ressaisie
 *
 *  Ce bloc n'offre AUCUN champ de saisie, et c'est le point : une incertitude
 *  tapée à la main serait fausse dès la première dérive et invérifiable. Le
 *  registre ISO-2 la calcule des points de contrôle interne, la fige avec ses
 *  entrées, et nomme les composantes qu'il n'a pas pu obtenir. Le dossier en
 *  prend une copie — y compris de ce qui manque.
 * ======================================================================== */

type NiveauU = {
  niveau: number; libelle: string; moyenne: number | null;
  ucPct: number | null; uPct: number | null; uAbsolue: number | null;
  couverture: string; sousEvaluee: boolean;
  manquantes: { cle: string; libelle: string; motif: string }[];
  composantes: { cle: string; libelle: string; disponible: boolean; valeurPct: number | null; motif: string }[];
  verdict: string;
};

function Incertitude({ dvmId, essai, peut, onFait }: {
  dvmId: string; essai: Essai; peut: boolean; onFait: () => void;
}) {
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const r = (essai.resultats || {}) as { ref?: string; dateEvaluation?: string; niveaux?: NiveauU[] };
  const niveaux = r.niveaux || [];

  const rattacher = () => run('u', async () => {
    setErreur('');
    const rep = await fetchEcriture(`/api/ged/ged/dvm/${dvmId}/incertitude`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    if (!rep.ok) { setErreur((await rep.json().catch(() => null))?.error?.message || 'Rattachement refusé.'); return; }
    onFait();
  });

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <span style={lbl}>Évaluation de l’incertitude</span>
      {niveaux.length === 0 ? (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Aucune évaluation rattachée. L’incertitude ne se saisit pas ici : elle est calculée par le
          registre des incertitudes à partir des points de contrôle interne, puis figée.
        </p>
      ) : (
        <>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Évaluation <span style={{ ...mono, fontWeight: 700 }}>{r.ref}</span> du {fmtDate(r.dateEvaluation || '')} — recopiée, elle ne se recalcule plus.
          </div>
          {niveaux.map((n) => (
            <div key={n.niveau} style={{ padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>{n.libelle}</span>
                <span style={mono}>u = {n.ucPct ?? '—'} %</span>
                <span style={{ ...mono, fontWeight: 700 }}>U = {n.uPct ?? '—'} %</span>
                {n.uAbsolue !== null && <span style={mono}>± {n.uAbsolue}</span>}
                <span style={{ flex: 1 }} />
                <Verdict v={n.verdict} />
              </div>
              {n.sousEvaluee && (
                <div style={{ fontSize: 12.5, color: 'var(--red-600)', lineHeight: 1.5 }}>
                  Sous-évaluée — {n.manquantes.map((x) => `${x.libelle} : ${x.motif}`).join(' ; ')}.
                  La composante absente n’a pas été remplacée par zéro. Cette réserve sera portée
                  dans le document.
                </div>
              )}
            </div>
          ))}
        </>
      )}
      {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
      {peut && (
        <div>
          <Bouton taille="sm" onClick={() => void rattacher()} disabled={estOccupe()}>
            {estOccupe() ? 'Rattachement…' : niveaux.length ? 'Rattacher la dernière évaluation' : 'Rattacher l’évaluation d’incertitude'}
          </Bouton>
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
 *  Une étape de l'assistant : une modalité du gabarit
 * ======================================================================== */

/* ═══════════════════════════════════════════════════════════════════════════
 * SUPPRESSION D'UNE SÉRIE (GED-16)
 *
 * `DELETE /ged/dvm/:id/serie/:serieId` était autorisée sans aucun écran. C'est
 * la SEULE VRAIE SUPPRESSION des trois routes du lot : le moteur fait
 * `documents().delete` — les mesures disparaissent — puis `recalculerEssai`,
 * qui refait le verdict de l'essai sans elles.
 *
 * ⚠️ Le moteur refuse déjà sur un dossier FIGÉ (apte · inapte · obsolète). Il
 * ne regarde en revanche PAS le document : `POST /ged/dvm/:id/document` écrit le
 * dossier dans un `ged-document` qui suit ensuite son propre circuit, et rien
 * n'empêche aujourd'hui de supprimer une série DÉJÀ IMPRIMÉE dans un document
 * en vigueur — moyenne, CV, verdict compris — alors que le dossier de méthode,
 * lui, est encore « en cours ». C'est une donnée opposable, et l'écran REFUSE :
 * il ne se contente pas d'avertir. Le refus est ici, côté écran, faute de
 * pouvoir toucher au moteur dans cette passe ; il est à porter côté serveur
 * (voir qa/AVANCEMENT-GED-16-SUPPRESSIONS.md).
 * ═════════════════════════════════════════════════════════════════════════ */

/** Les états dans lesquels un document est opposable à quelqu'un : il a été
 *  diffusé, ou il l'a été et ne l'est plus. Dans les trois cas, ce qu'il
 *  imprime a été lu — on ne retire pas le chiffre sous la page. */
const DOC_OPPOSABLE = ['envigueur', 'enrevision', 'retire'];

function consequencesSerie(
  s: Serie, e: Essai, m: Modalite, dd: Entete,
  doc: { code: string; statut: string; versionCourante?: string } | null,
): Consequence[] {
  const c: Consequence[] = [];
  if (dd.fige) {
    c.push({
      cle: 'fige', gravite: 'bloquant',
      texte: `Ce dossier est figé (« ${STATUTS[dd.statut]?.label || dd.statut} ») : ses mesures ne se suppriment plus.`,
      detail: 'Le moteur refuse également. Une évolution de la méthode impose une nouvelle révision du dossier, pas une correction de l’ancien.',
    });
  }
  if (doc && DOC_OPPOSABLE.includes(doc.statut)) {
    c.push({
      cle: 'doc-vigueur', gravite: 'bloquant',
      texte: `Cette série est imprimée dans le document ${doc.code}${doc.versionCourante ? ` v${doc.versionCourante}` : ''}, qui a été diffusé.`,
      detail: 'Le document cite n, moyenne, écart-type, CV et verdict de cette série. Supprimer la mesure sous une page déjà lue rendrait le document invérifiable : ce qu’un opérateur a accusé ne peut plus être défait ici. Passez par une révision du dossier.',
    });
  } else if (doc) {
    c.push({
      cle: 'doc-brouillon', gravite: 'attention',
      texte: `Le dossier a déjà été composé dans le document ${doc.code} (« ${doc.statut} »), qui cite cette série.`,
      detail: 'Le document n’est pas mis à jour par cette suppression : il faudra le recomposer, sinon il continuera d’afficher une série qui n’existe plus.',
    });
  }
  c.push({
    cle: 'mesures', gravite: 'attention',
    texte: `${s.mesures?.length ?? s.n ?? 0} mesure(s) sont effacées définitivement.`,
    detail: `${s.libelleNiveau || `Niveau ${s.niveau}`} — n = ${s.n ?? 0}, moyenne ${s.moyenne ?? '—'}, écart-type ${s.ecartType ?? '—'}, CV ${s.cv ?? '—'} %`
      + `${s.biais !== null && s.biais !== undefined ? `, biais ${s.biais} %` : ''}. `
      + 'Contrairement au retrait d’une ligne de grille, il n’y a pas de désactivation : la donnée disparaît de la base.',
  });
  const restantes = (e.series || []).filter((x) => x.documentId !== s.documentId);
  if (restantes.length === 0) {
    c.push({
      cle: 'dernier', gravite: 'attention',
      texte: `C’est la dernière série de « ${m.libelle} » : l’essai retombera sans verdict.`,
      detail: 'Sans verdict n’est pas conforme. Si cette modalité est obligatoire, elle redeviendra un blocage à la déclaration d’aptitude — c’est visible dans le bandeau des blocages.',
    });
  } else {
    c.push({
      cle: 'recalcul', gravite: 'info',
      texte: `Le verdict de « ${m.libelle} » (aujourd’hui : ${e.verdict === 'sans_verdict' ? 'sans verdict' : e.verdict.replace('_', ' ')}) est recalculé sur les ${restantes.length} série(s) restantes.`,
      detail: 'Il peut changer dans les deux sens — retirer la série la moins bonne rendrait l’essai conforme, ce qui est exactement le geste qu’un auditeur cherche.',
    });
  }
  c.push({
    cle: 'trace', gravite: 'info',
    texte: 'La suppression est inscrite au journal chaîné (DvmSerieSupprimee), avec le niveau et le n.',
    detail: 'La trace subsiste ; les valeurs, non. C’est la raison de ce panneau.',
  });
  return c;
}

/**
 * Ce que le contrôle interne dit DÉJÀ de la comparaison.
 *
 * Demande de la RQ du 14/08/2026 : utiliser les passages de CQI entre les deux
 * automates du couple miroir, ou entre deux opérateurs, pour alimenter les
 * mesures comparées.
 *
 * ── Trois partis pris d'affichage ──────────────────────────────────────────
 *
 *  1. L'AVERTISSEMENT VIENT AVANT LES CHIFFRES. Un tableau de 200 couples
 *     appariés, avec moyenne et écart-type, a toutes les apparences de l'étude
 *     de comparaison du §5.10 — laquelle demande 30 à 40 mesures appariées SUR
 *     ÉCHANTILLONS. Poser la réserve en note de bas de page, c'est laisser
 *     l'impression s'installer avant la nuance.
 *
 *  2. RIEN N'EST ÉCRIT DANS LE DOSSIER. Le panneau montre ; il n'importe pas.
 *     Ces couples éclairent la rédaction de la conclusion, ils ne remplissent
 *     pas une modalité à la place de son rédacteur.
 *
 *  3. L'ABSENCE EST EXPLIQUÉE. « Aucun couple » ne s'affiche jamais seul : le
 *     moteur dit POURQUOI — pas de carte, jamais le même jour, un seul
 *     opérateur — parce qu'un tableau vide se lit comme une panne.
 */
function MesuresComparees({ dvmId }: { dvmId: string }) {
  const [d, setD] = useState<null | {
    mode: string; avertissement: string; quoi: string; motif: string;
    couples: { date: string; niveau: number; libelleNiveau: string; a: number; b: number; ecart: number; sourceA: string; sourceB: string }[];
    stats: null | { n: number; moyenneEcart: number; ecartTypeEcart: number; min: number; max: number };
    periode: null | { debut: string; fin: string };
  }>(null);
  const [etat, setEtat] = useState<'attente' | 'chargement' | 'fait' | 'erreur'>('attente');
  const [tout, setTout] = useState(false);

  const charger = async () => {
    setEtat('chargement');
    try {
      const r = await fetch(`/api/ged/ged/dvm/${dvmId}/mesures-comparees`, { credentials: 'include' });
      if (!r.ok) { setEtat('erreur'); return; }
      setD(await r.json()); setEtat('fait');
    } catch { setEtat('erreur'); }
  };

  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '11px 13px', display: 'grid', gap: 9 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          Ce que le contrôle interne dit déjà
        </span>
        {etat === 'attente' && (
          <button type="button" onClick={() => void charger()}
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>
            Chercher les couples disponibles
          </button>
        )}
      </div>

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Les passages de CQI des deux appareils du couple miroir — ou de deux opérateurs — sont
        appariés par date et par niveau. Rien n’est écrit dans le dossier : c’est une lecture.
      </p>

      {etat === 'chargement' && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Recherche en cours…</span>}
      {etat === 'erreur' && <span style={{ fontSize: 12.5, color: 'var(--red-600)' }}>Les passages n’ont pas pu être lus.</span>}

      {etat === 'fait' && d && (
        <>
          <div style={{ background: 'var(--amber-50)', border: '1px solid var(--amber-200)', borderRadius: 'var(--radius-sm)', padding: '9px 11px', fontSize: 12.5, lineHeight: 1.55, color: 'var(--amber-800)' }}>
            {d.avertissement}
          </div>

          {d.mode === 'aucun' ? (
            <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>{d.motif}</div>
          ) : (
            <>
              <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                {d.quoi}{d.periode ? ` Du ${fmtDate(d.periode.debut)} au ${fmtDate(d.periode.fin)}.` : ''}
              </div>
              {d.stats && (
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                  <span>n = {d.stats.n}</span>
                  <span>écart moyen {d.stats.moyenneEcart}</span>
                  <span>ET des écarts {d.stats.ecartTypeEcart}</span>
                  <span>min {d.stats.min}</span>
                  <span>max {d.stats.max}</span>
                </div>
              )}
              <div style={{ maxHeight: tout ? 'none' : 210, overflow: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)' }}>
                {(tout ? d.couples : d.couples.slice(0, 12)).map((c, i) => (
                  <div key={`${c.date}-${c.niveau}-${i}`}
                    style={{ display: 'grid', gridTemplateColumns: '110px 1fr 90px 90px 90px', gap: 8, padding: '5px 9px', borderTop: i ? '1px solid var(--border-subtle)' : 'none', fontSize: 12.5 }}>
                    <span style={{ color: 'var(--text-muted)' }}>{fmtDate(c.date)}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{c.libelleNiveau}</span>
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{c.a}</span>
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{c.b}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', color: c.ecart === 0 ? 'var(--text-muted)' : 'var(--text-strong)' }}>
                      {c.ecart > 0 ? `+${c.ecart}` : c.ecart}
                    </span>
                  </div>
                ))}
              </div>
              {d.couples.length > 12 && (
                <button type="button" onClick={() => setTout((v) => !v)}
                  style={{ justifySelf: 'start', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>
                  {tout ? 'Réduire' : `Voir les ${d.couples.length} couples`}
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * §5 — LES MODALITÉS, RANGÉES SELON CE QUE LE DOSSIER COUVRE.
 *
 * Trois rendus, et un seul est un choix :
 *
 *  • un paramètre  → la liste, comme depuis toujours. Aucune notion de portée
 *    n'apparaît : elle n'aurait rien à désigner.
 *  • plusieurs paramètres, présentation « tableau » → la même liste, chaque
 *    ligne portant la pastille de ce qu'elle vise. C'est la lecture par
 *    MODALITÉ : « la répétabilité, où en est-elle ? »
 *  • plusieurs paramètres, présentation « sections » → un bloc par paramètre,
 *    puis un bloc commun. C'est la lecture par PARAMÈTRE : « le potassium,
 *    est-il validé ? »
 *
 * La règle qui décide (RQ, 25/08/2026) : « présentées ensemble ou l'une à la
 * suite de l'autre selon ma situation ». Le réglage est pris à la création du
 * DVM et ne se devine pas ici.
 *
 * ── Le piège évité ─────────────────────────────────────────────────────────
 * En sections, une modalité NON RENSEIGNÉE n'a pas encore de portée. La ranger
 * sous chaque paramètre donnerait N lignes qui modifient toutes le MÊME essai :
 * on croirait avoir treize répétabilités à faire, et remplir l'une viderait les
 * autres de leur sens. Elles restent donc dans le bloc commun, d'où on les
 * affecte en déclarant leur portée.
 */
function Performances({ d, dd, id, recharger }: {
  d: Dossier; dd: Entete; id: string; recharger: () => void;
}) {
  const params = dd.parametres || [];
  const etape = (m: Modalite) => (
    <Etape key={m.type} dvmId={id} m={m} referentiel={d.referentiel}
      dd={dd} peutEcrire={d.peutEcrire} onFait={recharger} />
  );

  if (params.length < 2 || dd.presentation !== 'sections') return <>{d.grille.map(etape)}</>;

  const communes = d.grille.filter((m) => !m.essai?.parametreCode);
  return (
    <>
      {params.map((p) => {
        const siennes = d.grille.filter((m) => m.essai?.parametreCode === p.code);
        return (
          <div key={p.code}>
            <div style={{ borderTop: '1px solid var(--border-default)', background: 'var(--cyan-50)', padding: '9px 14px', display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ ...mono, fontWeight: 700, color: 'var(--cyan-700)' }}>{p.code}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{p.libelle || p.code}</span>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                {p.techniqueLibelle || p.techniqueCode || 'technique non précisée'}
                {p.equipementCode ? ` · ${p.equipementCode}` : ''}
              </span>
            </div>
            {siennes.length ? siennes.map(etape) : (
              <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '10px 14px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                Aucun essai ne porte spécifiquement sur ce paramètre. Les essais communs ci-dessous
                lui sont appliqués — pour en réserver un à ce paramètre, ouvrez-le et déclarez sa portée.
              </div>
            )}
          </div>
        );
      })}

      <div style={{ borderTop: '1px solid var(--border-default)', background: 'var(--gray-25)', padding: '9px 14px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Commun à tous les paramètres</div>
        <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Ces modalités qualifient la méthode et non un analyte — contamination, robustesse,
          stabilité. Les modalités encore vides s’y trouvent aussi : leur portée se déclare
          en les renseignant.
        </p>
      </div>
      {communes.map(etape)}
    </>
  );
}

function Etape({ dvmId, m, referentiel, dd, peutEcrire, onFait }: {
  dvmId: string; m: Modalite; referentiel: Dossier['referentiel'];
  dd: Entete; peutEcrire: boolean; onFait: () => void;
}) {
  const e = m.essai;
  const fige = dd.fige;
  const { docs } = useGed();
  const [ouvert, setOuvert] = useState(false);
  const [serieEditee, setSerieEditee] = useState<Serie | null>(null);
  const [serieSupprimee, setSerieSupprimee] = useState<Serie | null>(null);
  const [nouvelleSerie, setNouvelleSerie] = useState(false);
  const [erreur, setErreur] = useState('');
  const [f, setF] = useState(() => ({
    applicable: e ? e.applicable : true,
    parametreCode: e?.parametreCode || '',
    matriceCode: e?.matriceCode || '',
    justificationNonApplicable: e?.justificationNonApplicable || '',
    typeVerification: e?.typeVerification || '',
    conclusion: e?.conclusion || '',
  }));
  /* L'essai LU à l'ouverture (14/10/2026) : seuls les champs changés depuis s'écrivent. */
  const [base] = useState(() => ({
    applicable: e ? e.applicable : true, parametreCode: e?.parametreCode || '', matriceCode: e?.matriceCode || '',
    justificationNonApplicable: e?.justificationNonApplicable || '', typeVerification: e?.typeVerification || '', conclusion: e?.conclusion || '',
  }));
  const { run, estOccupe } = useBusy();
  const maj = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));

  const enregistrer = () => run('save', async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/dvm/${dvmId}/essai`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...f, type: m.type, base }),
    });
    if (!r.ok) { setErreur((await r.json().catch(() => null))?.error?.message || 'Enregistrement refusé.'); return; }
    onFait();
  });

  const exigeSeries = ['repetabilite', 'fidelite_intermediaire', 'justesse', 'exactitude'].includes(m.type);
  const estComparaison = m.type === 'comparaison_methodes';
  const renseigne = e && (e.applicable === false
    ? !!String(e.justificationNonApplicable || '').trim()
    : !!String(e.conclusion || '').trim());

  const badgeExigence = m.exigence === 'obligatoire_experimental'
    ? pastille({ label: 'obligatoire — étude expérimentale', bg: 'var(--red-50)', fg: 'var(--red-600)' })
    : m.exigence === 'obligatoire'
      ? pastille({ label: 'obligatoire', bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' })
      : pastille({ label: 'facultatif', bg: 'var(--gray-50)', fg: 'var(--text-muted)' });

  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '12px 14px' }}>
      <button onClick={() => setOuvert((o) => !o)}
        style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', textAlign: 'start' }}>
        <I n={ouvert ? 'chevron-bas' : 'chevron-right'} s={14} />
        <span style={{ ...mono, fontWeight: 700, color: 'var(--color-primary-active)' }}>§{m.section}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{m.element}. {m.libelle}</span>
        {badgeExigence}
        {/* La portée n'apparaît QUE sur un dossier multi-paramètres, et
            seulement une fois l'essai renseigné : sur une ligne encore vide,
            « tous les paramètres » se lirait comme une décision prise. */}
        {(dd.matrices?.length || 0) > 1 && e && pastille(e.matriceCode
          ? { label: dd.matrices!.find((m) => m.code === e.matriceCode)?.libelle || e.matriceCode, bg: 'var(--green-50)', fg: 'var(--green-700)' }
          : { label: 'toutes les matières', bg: 'var(--gray-50)', fg: 'var(--text-muted)' })}
        {(dd.parametres?.length || 0) > 1 && e && pastille(e.parametreCode
          ? { label: e.parametreCode, bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' }
          : { label: 'tous les paramètres', bg: 'var(--gray-50)', fg: 'var(--text-muted)' })}
        <span style={{ flex: 1 }} />
        {e && e.applicable === false && <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>non applicable</span>}
        {e && e.applicable !== false && <Verdict v={e.verdict} />}
        {!renseigne && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)' }}>à renseigner</span>}
      </button>

      {ouvert && (
        <div style={{ marginTop: 12, marginInlineStart: 24, display: 'grid', gap: 12 }}>
          <div style={{ background: 'var(--cyan-50)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body)' }}>
            <div><strong style={{ color: 'var(--text-strong)' }}>Ce qui est attendu.</strong> {m.attendu}</div>
            <div style={{ marginTop: 4 }}><strong style={{ color: 'var(--text-strong)' }}>Pourquoi.</strong> {m.pourquoi}</div>
          </div>

          {peutEcrire && !fige && (
            <>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5 }}>
                  <input type="checkbox" checked={f.applicable} onChange={(ev) => maj('applicable', ev.target.checked)} />
                  Applicable
                </label>

                {/* ── SUR QUELLE MATIÈRE PORTE CET ESSAI (26/08/2026) ────────
                    Le cœur du cas « extraction ». Une charge virale CMV
                    validée sur plasma dit tout de l'amplification et RIEN du
                    rendement d'extraction sur LCR.

                    « Toutes les matières » convient à la répétabilité, à la
                    justesse, à la linéarité — tout ce qui se passe APRÈS
                    l'extraction. Une matière désignée convient au rendement
                    d'extraction, aux inhibiteurs et à la contamination : ce
                    qui n'existe que tant que le prélèvement est encore
                    lui-même. */}
                {(dd.matrices?.length || 0) > 1 && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Matière :</span>
                    <select aria-label="Matière visée par cet essai" style={{ ...inputStyle, width: 230 }}
                      value={f.matriceCode}
                      onChange={(ev) => maj('matriceCode', ev.target.value)}>
                      <option value="">toutes les matières (après extraction)</option>
                      {(dd.matrices || []).map((m) => (
                        <option key={m.code} value={m.code}>{m.libelle}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* ── SUR QUOI PORTE CET ESSAI (25/08/2026) ──────────────────
                    Affiché SEULEMENT si le dossier couvre plusieurs paramètres :
                    sur un dossier à un paramètre, la question n'a pas de sens et
                    le champ ferait croire à un réglage oublié. */}
                {(dd.parametres?.length || 0) > 1 && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Porte sur :</span>
                    <select aria-label="Paramètre visé par cet essai" style={{ ...inputStyle, width: 210 }}
                      value={f.parametreCode}
                      onChange={(ev) => maj('parametreCode', ev.target.value)}>
                      <option value="">tous les paramètres du dossier</option>
                      {(dd.parametres || []).map((p) => (
                        <option key={p.code} value={p.code}>{p.code}{p.libelle ? ` — ${p.libelle}` : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Type de vérification :</span>
                  <select aria-label="Type de vérification" style={{ ...inputStyle, width: 190 }} value={f.typeVerification}
                    onChange={(ev) => maj('typeVerification', ev.target.value)}>
                    <option value="">— à préciser —</option>
                    <option value="bibliographique">Bibliographique</option>
                    <option value="essais">Par essais</option>
                  </select>
                </div>
              </div>

              {!f.applicable && (
                <div>
                  <label style={lbl} htmlFor={`j-${m.type}`}>Justification de la non-applicabilité</label>
                  <textarea id={`j-${m.type}`} rows={2} style={inputStyle} value={f.justificationNonApplicable}
                    onChange={(ev) => maj('justificationNonApplicable', ev.target.value)}
                    placeholder="Le formulaire écrit « non applicable (à justifier) » : sans motif écrit, la case n’est pas opposable." />
                </div>
              )}
            </>
          )}

          {estComparaison && <MesuresComparees dvmId={dvmId} />}

          {exigeSeries && e && (
            <div>
              <span style={lbl}>Séries de mesures</span>
              {e.series.length === 0 && (
                <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-muted)' }}>
                  Aucune mesure saisie. Cette modalité restera sans verdict tant qu’aucune valeur ne
                  sera confrontée à un critère.
                </p>
              )}
              {e.series.map((s) => (
                /* Le clic ouvre la série ; « Supprimer » reste, il passe par
                   une suppression raisonnée (Ines, 26/08/2026). */
                <div key={s.documentId}
                  {...(peutEcrire && !fige
                    ? ligneOuvrable(
                      () => { setNouvelleSerie(false); setSerieSupprimee(null); setSerieEditee(s); },
                      `Modifier ${s.libelleNiveau || `Niveau ${s.niveau}`}`,
                      { display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap', padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' },
                    )
                    : { style: { display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap', padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' } })}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>
                    {s.libelleNiveau || `Niveau ${s.niveau}`}
                  </span>
                  <span style={mono}>n = {s.n ?? 0}</span>
                  <span style={mono}>moyenne {s.moyenne ?? '—'}</span>
                  <span style={mono}>ET {s.ecartType ?? '—'}</span>
                  <span style={{ ...mono, fontWeight: 700 }}>CV {s.cv ?? '—'} %</span>
                  {s.biais !== null && <span style={mono}>biais {s.biais} %</span>}
                  {s.niveauSfbc && (
                    <span style={{ fontSize: 12.5, color: s.niveauSfbcConfirme ? 'var(--text-muted)' : 'var(--amber-600)' }}>
                      colonne {NIVEAUX_REF[s.niveauSfbc] || s.niveauSfbc}{s.niveauSfbcConfirme ? '' : ' — non confirmée'}
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  <Verdict v={s.verdict} seuil={s.seuilApplique} />
                  {peutEcrire && !fige && (
                    <SansOuvrir>
                      {/* Icône seule (règle du 28/08) : la corbeille se répète
                          à chaque série et son geste est univoque. Le niveau
                          est nommé dans l'action, sinon toutes les corbeilles
                          de l'écran porteraient le même nom au clavier. */}
                      <BoutonIcone icone="trash" variante="danger" taille={13}
                        action={`Supprimer ${s.libelleNiveau || `le niveau ${s.niveau}`}`}
                        onClick={() => { setNouvelleSerie(false); setSerieEditee(null); setSerieSupprimee(s); }} />
                    </SansOuvrir>
                  )}
                </div>
              ))}
              {serieSupprimee && e && (
                <div style={{ marginTop: 10 }}>
                  <SuppressionRaisonnee
                    nature="suppression"
                    titre="Supprimer une série de mesures"
                    objet={<>
                      <strong>{serieSupprimee.libelleNiveau || `Niveau ${serieSupprimee.niveau}`}</strong>
                      {' — '}modalité « {m.libelle} » du dossier {dd.ref}.
                    </>}
                    consequences={consequencesSerie(
                      serieSupprimee, e, m, dd,
                      dd.docCode ? (docs.find((x) => x.code === dd.docCode) || null) : null,
                    )}
                    libelleAction="Supprimer la série"
                    onConfirmer={async () => {
                      const r = await fetchEcriture(`/api/ged/ged/dvm/${dvmId}/serie/${serieSupprimee.documentId}`, {
                        method: 'DELETE', credentials: 'include',
                      });
                      if (!r.ok) return (await r.json().catch(() => null))?.error?.message || 'Suppression refusée.';
                      setSerieSupprimee(null);
                      onFait();
                      return '';
                    }}
                    onAnnuler={() => setSerieSupprimee(null)}
                    note={<>
                      Corriger une valeur mal saisie se fait par <strong>Modifier</strong> : la série est
                      réenregistrée, ses statistiques et son verdict sont recalculés, et le dossier garde
                      la trace du changement. La suppression n’est utile que pour une série qui{' '}
                      <em>n’aurait jamais dû être saisie</em> — mauvais analyte, mauvais équipement,
                      essai abandonné.
                    </>}
                  />
                </div>
              )}
              {peutEcrire && !fige && !nouvelleSerie && !serieEditee && !serieSupprimee && (
                <Bouton taille="sm" onClick={() => setNouvelleSerie(true)} style={{ marginTop: 8 }}>
                  <I n="plus" s={13} />Ajouter un niveau
                </Bouton>
              )}
              {(nouvelleSerie || serieEditee) && (
                <div style={{ marginTop: 10 }}>
                  <SerieForm dvmId={dvmId} essai={e} serie={serieEditee} referentiel={referentiel}
                    onFait={() => { setNouvelleSerie(false); setSerieEditee(null); onFait(); }}
                    onAnnule={() => { setNouvelleSerie(false); setSerieEditee(null); }} />
                </div>
              )}
            </div>
          )}

          {/* §5.8 — l'incertitude ne se SAISIT pas : elle se rattache. Le registre
              ISO-2 l'a calculée des points de contrôle interne et l'a figée, avec
              ses composantes ET le motif de celles qui manquent. */}
          {m.type === 'incertitude' && e && (
            <Incertitude dvmId={dvmId} essai={e} peut={peutEcrire && !fige} onFait={onFait} />
          )}

          {e?.criteres && (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Critère appliqué et <strong>figé</strong> : {String(e.sourceCritere || '?')} version {String(e.versionCritere || '?')}
              {e.referenceCritere ? ` — ${e.referenceCritere}` : ''}
            </div>
          )}

          {peutEcrire && !fige && (
            <>
              <div>
                <label style={lbl} htmlFor={`c-${m.type}`}>Conclusion et commentaire</label>
                <textarea id={`c-${m.type}`} rows={2} style={inputStyle} value={f.conclusion}
                  onChange={(ev) => maj('conclusion', ev.target.value)} />
              </div>
              {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
              <div>
                <Bouton variante="primaire" taille="sm" onClick={() => void enregistrer()} disabled={estOccupe()}>
                  <I n="check" s={13} />{estOccupe('save') ? 'Enregistrement…' : 'Enregistrer la modalité'}
                </Bouton>
              </div>
            </>
          )}

          {fige && e && (
            <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
              {e.applicable === false
                ? <><strong>Non applicable.</strong> {e.justificationNonApplicable}</>
                : e.conclusion || <em style={{ color: 'var(--text-muted)' }}>aucune conclusion écrite</em>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
 *  Le dossier : en-tête, fiche méthode, grille, décision
 * ======================================================================== */

/* ==========================================================================
 *  §4, §6, §10 — les rubriques du corps du formulaire
 *
 *  Elles ne portent aucun calcul, et c'est précisément pourquoi elles étaient
 *  absentes de V1 : rien ne les réclamait à l'écran. Le gabarit, lui, les donne
 *  obligatoires sans condition — une méthode déclarée apte sans analyse de ses
 *  points critiques n'a été jugée que sur ses chiffres.
 * ======================================================================== */

function Rubriques({ dvmId, dd, peut, onFait, ouvert, basculer }: {
  dvmId: string; dd: Entete; peut: boolean; onFait: () => void;
  ouvert: boolean; basculer: () => void;
}) {
  const [edite, setEdite] = useState(false);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const { settings } = useGed();
  const [risques, setRisques] = useState<LigneRisque[]>(() =>
    FAMILLES_5M.map(([cle]) => (dd.maitriseRisques?.lignes || []).find((l) => l.famille === cle) || { famille: cle }));
  const [info, setInfo] = useState<Record<string, string>>(() => ({ ...(dd.donneesInformatiques || {}) }));
  const [suivi, setSuivi] = useState<Record<string, string>>(() => ({ ...(dd.elementsSuivi || {}) }));
  const lignesUtiles = (l: LigneRisque[]) => l.filter((x) => (x.pointsCritiques || '').trim() || (x.donneesEntree || '').trim() || (x.remarques || '').trim());
  /* Les rubriques LUES à l'ouverture (14/10/2026), dans la forme où on les renvoie : le serveur
     n'écrit que les sous-champs changés depuis, et refuse (409) ceux qu'un autre a changés. */
  const [base] = useState(() => ({
    maitriseRisques: { ...(dd.maitriseRisques || {}), lignes: lignesUtiles(FAMILLES_5M.map(([cle]) => (dd.maitriseRisques?.lignes || []).find((l) => l.famille === cle) || { famille: cle })) },
    donneesInformatiques: { ...(dd.donneesInformatiques || {}) }, elementsSuivi: { ...(dd.elementsSuivi || {}) },
  }));

  // ⚠️ Une ligne encore marquée « proposée » n'est PAS une étape analysée :
  // personne ne l'a lue. La compter ferait afficher « 9 étapes analysées » sur
  // un §4 que le bouton a rempli tout seul.
  const remplies = (dd.maitriseRisques?.lignes || []).filter((l) => !l.propose && ((l.pointsCritiques || '').trim() || (l.donneesEntree || '').trim()));
  const INFO_CHAMPS: [string, string][] = [
    ['transfertAutomate', 'Transfert automate ↔ middleware'],
    ['transfertSil', 'Transfert middleware ↔ SIL'],
    ['algorithme', 'Algorithme décisionnel / règles d’expertise'],
    ['arrondissage', 'Calculs et règles d’arrondissage'],
    ['saisiesManuelles', 'Saisies manuelles'],
    ['dossiersTests', 'Dossiers patients tests'],
  ];
  const SUIVI_CHAMPS: [string, string][] = [
    ['reglesCiq', 'Règles de suivi par le contrôle interne'],
    ['exploitationEeq', 'Exploitation des évaluations externes'],
    ['suiviIncertitude', 'Suivi de l’incertitude et de l’analyse de risque'],
    ['dysfonctionnements', 'Dysfonctionnements et analyses de tendance'],
  ];

  /** Reprendre le fonds de points critiques bio24 dans les lignes VIDES.
   *
   *  Jamais dans une ligne déjà écrite : la proposition ne doit pas pouvoir
   *  effacer une analyse. C'est aussi ce qui rend le bouton réutilisable sans
   *  danger quand on revient sur un dossier commencé. */
  const proposer = () => {
    const fonds = (settings?.pointsCritiques5m || []) as LigneRisque[];
    setRisques((x) => x.map((l) => {
      if ((l.pointsCritiques || '').trim() || (l.donneesEntree || '').trim()) return l;
      const p = fonds.find((f) => f.famille === l.famille);
      if (!p) return l;
      return { ...l, donneesEntree: p.donneesEntree, pointsCritiques: p.pointsCritiques, maitrise: p.maitrise, propose: true };
    }));
  };

  const enregistrer = () => run('save', async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/dvm/${dvmId}`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        maitriseRisques: { ...(dd.maitriseRisques || {}), lignes: lignesUtiles(risques) },
        donneesInformatiques: info, elementsSuivi: suivi, base,
      }),
    });
    if (!r.ok) { setErreur((await r.json().catch(() => null))?.error?.message || 'Enregistrement refusé.'); return; }
    setEdite(false); onFait();
  });

  const vide = (t: string) => <span style={{ color: 'var(--red-600)' }}>{t}</span>;

  /* Trois rubriques du gabarit tenues ensemble parce qu'elles se remplissent
     d'un même geste. « À compléter » dès qu'une des trois est vide : le §4 est
     obligatoire sans condition, et un §6 muet laisse hors dossier le chemin que
     suit le résultat jusqu'au patient. */
  const complet = !!remplies.length
    && INFO_CHAMPS.some(([k]) => (dd.donneesInformatiques?.[k] || '').trim())
    && SUIVI_CHAMPS.some(([k]) => (dd.elementsSuivi?.[k] || '').trim());

  return (
    <SectionDvm num="§4 · §6 · §10" titre="Risques, informatique, suivi"
      etat={complet ? 'complet' : 'a_completer'}
      resume={remplies.length ? `${remplies.length} étape(s) de la grille 5M analysée(s)` : 'grille 5M non analysée'}
      ouvert={ouvert} basculer={basculer}
      enfants={(
    <div style={{ padding: 14, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ flex: 1 }} />
        {peut && !edite && <Bouton taille="sm" onClick={() => setEdite(true)}><I n="pen" s={13} />Modifier</Bouton>}
      </div>

      {!edite ? (
        <div style={{ display: 'grid', gap: 10, fontSize: 12.5, lineHeight: 1.55 }}>
          <div>
            <span style={lbl}>§4 — Maîtrise des risques (grille 5M)</span>
            {remplies.length
              ? <span style={{ color: 'var(--text-strong)' }}>{remplies.length} étape(s) analysée(s) : {remplies.map((l) => FAMILLES_5M.find(([c]) => c === l.famille)?.[1] || l.famille).join(', ')}.</span>
              : vide('Aucune analyse des points critiques : le gabarit la rend obligatoire, sans condition de portée ni de type de résultat.')}
          </div>
          <div>
            <span style={lbl}>§6 — Données informatiques</span>
            {INFO_CHAMPS.some(([k]) => (dd.donneesInformatiques?.[k] || '').trim())
              ? <span style={{ color: 'var(--text-strong)' }}>{INFO_CHAMPS.filter(([k]) => (dd.donneesInformatiques?.[k] || '').trim()).map(([, l]) => l).join(' · ')}</span>
              : vide('Aucun transfert ni règle d’arrondissage décrit — c’est pourtant par là que passe le résultat rendu au patient.')}
          </div>
          <div>
            <span style={lbl}>§10 — Éléments de suivi</span>
            {SUIVI_CHAMPS.some(([k]) => (dd.elementsSuivi?.[k] || '').trim())
              ? <span style={{ color: 'var(--text-strong)' }}>{SUIVI_CHAMPS.filter(([k]) => (dd.elementsSuivi?.[k] || '').trim()).map(([, l]) => l).join(' · ')}</span>
              : vide('Rien n’est déclaré sur la surveillance de la méthode après sa mise en service.')}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>§4 — Points critiques, famille par famille</div>
              {!!(settings?.pointsCritiques5m || []).length && (
                <button type="button" onClick={proposer}
                  title="Reprend les points critiques types de bio24 dans les familles encore vides. Rien n’est écrasé, et une ligne reprise reste marquée « proposée » tant que vous ne l’avez pas validée."
                  style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>
                  Proposer les points critiques bio24
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              {risques.map((l, i) => (
                <div key={l.famille} style={{
                  display: 'grid', gap: 5,
                  /* Une proposition non reprise se voit : fond neutre et filet
                     ambre. Sans marque visible, elle se confondrait avec une
                     analyse écrite par le laboratoire — la seule chose que ce
                     lot devait empêcher. */
                  ...(l.propose ? { background: 'var(--gray-25)', borderInlineStart: '3px solid var(--amber-400)', padding: '7px 9px', borderRadius: '0 var(--radius-sm) var(--radius-sm) 0' } : {}),
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 1fr) 2fr 2fr 70px 2fr', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{FAMILLES_5M[i][1]}</span>
                    {(['donneesEntree', 'pointsCritiques'] as const).map((k) => (
                      <input key={k} style={inputStyle} value={l[k] || ''}
                        aria-label={`${FAMILLES_5M[i][1]} — ${k === 'donneesEntree' ? 'données d’entrée' : 'points critiques'}`}
                        placeholder={k === 'donneesEntree' ? 'données d’entrée' : 'points critiques à maîtriser'}
                        onChange={(ev) => setRisques((x) => x.map((y, j) => (j === i ? { ...y, [k]: ev.target.value } : y)))} />
                    ))}
                    <input style={inputStyle} value={l.criticite || ''} aria-label={`${FAMILLES_5M[i][1]} — criticité`} placeholder="critic."
                      onChange={(ev) => setRisques((x) => x.map((y, j) => (j === i ? { ...y, criticite: ev.target.value } : y)))} />
                    <input style={inputStyle} value={l.maitrise || ''} aria-label={`${FAMILLES_5M[i][1]} — modalités de maîtrise`} placeholder="modalités de maîtrise (réf. procédure)"
                      onChange={(ev) => setRisques((x) => x.map((y, j) => (j === i ? { ...y, maitrise: ev.target.value } : y)))} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 1fr) 1fr', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>
                      {l.propose ? 'proposition — à reprendre à votre compte' : ''}
                    </span>
                    <input style={inputStyle} value={l.remarques || ''}
                      aria-label={`${FAMILLES_5M[i][1]} — remarques propres à cette technique`}
                      placeholder="remarques propres à CETTE technique"
                      onChange={(ev) => setRisques((x) => x.map((y, j) => (j === i ? { ...y, remarques: ev.target.value } : y)))} />
                  </div>
                  {l.propose && (
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: 'var(--amber-800)' }}>
                      <input type="checkbox" checked={false}
                        onChange={() => setRisques((x) => x.map((y, j) => (j === i ? { ...y, propose: false } : y)))} />
                      J’ai relu ce point pour cette technique et je le reprends à mon compte
                    </label>
                  )}
                </div>
              ))}
            </div>
            {risques.some((l) => l.propose) && (
              <div style={{ marginTop: 7, fontSize: 12.5, color: 'var(--amber-800)', lineHeight: 1.5 }}>
                {risques.filter((l) => l.propose).length} famille(s) encore à l’état de proposition : elles ne comptent
                pas comme analysées et n’apparaîtront pas dans le dossier composé tant qu’elles n’auront pas été reprises.
              </div>
            )}
          </div>
          {([['§6 — Données informatiques', INFO_CHAMPS, info, setInfo], ['§10 — Éléments de suivi', SUIVI_CHAMPS, suivi, setSuivi]] as const).map(([titre, champs, valeurs, poser]) => (
            <div key={titre}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)', marginBottom: 6 }}>{titre}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 8 }}>
                {champs.map(([k, label]) => (
                  <div key={k}>
                    <label style={lbl} htmlFor={`r-${k}`}>{label}</label>
                    <input id={`r-${k}`} style={inputStyle} value={valeurs[k] || ''}
                      onChange={(ev) => poser((x) => ({ ...x, [k]: ev.target.value }))} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
          <PiedActions taille="sm" annuler={{ onClick: () => setEdite(false) }} principale={{ libelle: "Enregistrer", disabled: estOccupe(), onClick: () => void enregistrer() }} />
        </div>
      )}
    </div>
      )} />
  );
}

/* ==========================================================================
 *  Le LIVRABLE — porter le dossier dans la GED
 *
 *  Ce n'est pas un export. Le bouton compose le corps du dossier dans une
 *  version d'un document maîtrisé ordinaire, qui prend ensuite son code, sa
 *  version, son circuit, son cartouche et sa diffusion de la chaîne
 *  documentaire. Aucun téléchargement n'est proposé ici : l'aperçu officiel
 *  du document est le seul chemin d'impression, et il barre « NE PAS
 *  APPLIQUER » tout ce qui n'est pas en vigueur.
 * ======================================================================== */

function DansLaGed({ dvmId, dd, chapitres, reserves, onFait }: {
  dvmId: string; dd: Entete; chapitres: Chapitre[]; reserves: Reserve[]; onFait: () => void;
}) {
  const { settings, openDoc } = useGed();
  const [verificateur, setVerificateur] = useState('');
  const [approbateur, setApprobateur] = useState('');
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const { run, estOccupe } = useBusy();
  const users = settings?.gedUsers || [];
  const incomplets = chapitres.filter((c) => !c.complet);

  const composer = () => run('doc', async () => {
    setErreur(''); setMessage('');
    const r = await fetchEcriture(`/api/ged/ged/dvm/${dvmId}/document`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ verificateur, approbateur }),
    });
    const j = await r.json().catch(() => null);
    if (!r.ok) { setErreur(j?.error?.message || 'Composition refusée.'); return; }
    setMessage(String(j?.message || ''));
    onFait();
  });

  return (
    <div style={{ ...card, padding: 14, display: 'grid', gap: 11 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Le dossier dans la GED</div>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
        Le dossier de {dd.mot} n’est pas un export : c’est un <strong>document maîtrisé</strong>. Il prend
        un code, une version, un rédacteur, un vérificateur, un approbateur, il suit le circuit, il se
        diffuse et il s’archive comme les autres. Le cartouche et la pagination sont posés par la
        plateforme à l’aperçu — ils ne se saisissent nulle part.
      </p>

      {/* Ce que le document dira de lui-même, AVANT de le composer. */}
      <div style={{ display: 'grid', gap: 5, fontSize: 12.5, lineHeight: 1.5 }}>
        {incomplets.length ? (
          <>
            <div style={{ fontWeight: 700, color: 'var(--red-600)' }}>
              {incomplets.length} chapitre{incomplets.length > 1 ? 's sortiront' : ' sortira'} INCOMPLET{incomplets.length > 1 ? 'S' : ''} — le document le portera en toutes lettres.
            </div>
            <ul style={{ margin: 0, paddingInlineStart: 18, color: 'var(--text-body)' }}>
              {incomplets.map((c) => (
                <li key={`${c.num}-${c.titre}`}><strong>{c.num} {c.titre}</strong> — {c.motif}</li>
              ))}
            </ul>
          </>
        ) : (
          <div style={{ color: 'var(--green-600)', fontWeight: 600 }}>Tous les chapitres du gabarit sont renseignés.</div>
        )}
      </div>

      {reserves.length > 0 && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, lineHeight: 1.55 }}>
          <strong>{reserves.length} réserve{reserves.length > 1 ? 's' : ''} seront reportées dans le document.</strong>{' '}
          Ce que le laboratoire n’a pas, le dossier ne l’invente pas : il l’écrit, en tête.
          <ul style={{ margin: '5px 0 0', paddingInlineStart: 18 }}>
            {reserves.slice(0, 4).map((r, i) => <li key={i}><strong>{r.portee}</strong> — {r.texte}</li>)}
            {reserves.length > 4 && <li>… et {reserves.length - 4} autre(s).</li>}
          </ul>
        </div>
      )}

      {dd.docCode ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.55 }}>
          Porté dans la GED sous <span style={{ ...mono, fontWeight: 700 }}>{dd.docCode}</span>.{' '}
          {/* PLEINE PAGE depuis le 07/10/2026, comme partout ailleurs où l'on
              ouvre un document : une seule règle à retenir. Le fil d'Ariane
              ramène au dossier de vérification, position de lecture comprise. */}
          <Bouton taille="sm" onClick={() => openDoc(dd.docCode as string)}>Ouvrir le document</Bouton>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          {([['Vérificateur (facultatif)', verificateur, setVerificateur], ['Approbateur', approbateur, setApprobateur]] as const).map(([label, v, poser]) => (
            <div key={label}>
              <label style={lbl} htmlFor={`circ-${label}`}>{label}</label>
              <select id={`circ-${label}`} style={inputStyle} value={v} onChange={(e) => poser(e.target.value)}>
                <option value="">—</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}

      {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)', lineHeight: 1.5 }}>{erreur}</div>}
      {message && <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>{message}</div>}

      <div>
        <Bouton variante="primaire" onClick={() => void composer()} disabled={estOccupe()}>
          <I n="doc" s={14} />
          {estOccupe() ? 'Composition…' : dd.docCode ? 'Recomposer le brouillon' : 'Constituer le dossier dans la GED'}
        </Bouton>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
 *  LE DOSSIER COMME UN DOCUMENT — sections empilées, dépliables
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Demande de la RQ, 25/08/2026 : « je voudrais me balader dans l'app comme si
 * j'étais dans Word. La page du DVM est une sorte d'empilement de sections que
 * je peux déplier pour remplir. »
 *
 * Ce que ça change par rapport à des cartes ordinaires : le dossier a la MÊME
 * table des matières à l'écran et à l'impression. Les numéros de section sont
 * ceux du gabarit TUNAC, ceux que `composerDossier` écrit dans le document et
 * que `dvm-docx` transforme en sommaire cliquable. Un §7 vu à l'écran est le §7
 * du Word — sans quoi la RQ devrait apprendre deux plans pour un seul dossier.
 *
 * ── L'état de pliage ────────────────────────────────────────────────────────
 *
 * Il est retenu PAR DOSSIER dans la session : on quitte un dossier pour aller
 * chercher un numéro de lot, on revient, on retrouve sa page ouverte au même
 * endroit. `sessionStorage` et non `localStorage` : c'est une position de
 * lecture, pas une préférence — la retrouver dans six mois n'aurait aucun sens.
 *
 * Le défaut n'est pas « tout replié » : une section qui réclame quelque chose
 * s'ouvre d'elle-même. Tout replier ferait d'un dossier vide et d'un dossier
 * complet la même page, et il faudrait treize clics pour découvrir qu'il
 * manque une justification.
 */
type EtatSection = 'a_completer' | 'complet' | 'repris';

/** L'ordre du gabarit — et l'ordre du document imprimé. « Tout replier » doit
 *  connaître les sections nommément : replier ce qui est monté à l'écran
 *  laisserait ouvert ce qui ne l'est pas encore. */
const NUMEROS_SECTIONS = ['§0', '§1', '§2', '§3', '§4·6·10', '§5', '§8', '§11'];

function badgeEtat(etat: EtatSection) {
  if (etat === 'repris') return pastille({ label: 'repris de l’application', bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' });
  if (etat === 'a_completer') return pastille({ label: 'à compléter', bg: 'var(--amber-50)', fg: 'var(--amber-700)' });
  return pastille({ label: 'renseigné', bg: 'var(--green-50)', fg: 'var(--green-700)' });
}

function SectionDvm({ num, titre, etat, resume, ouvert, basculer, enfants }: {
  num: string; titre: string; etat: EtatSection;
  /** Une ligne visible SANS déplier — sinon replier revient à cacher. */
  resume?: React.ReactNode;
  ouvert: boolean; basculer: () => void; enfants: React.ReactNode;
}) {
  const cle = `sect-${num.replace(/[^\w]/g, '')}`;
  /* ⚠️ PAS d'`overflow: hidden` sur la carte, malgré les coins arrondis : le §2
     contient le sélecteur d'appareil miroir, dont la liste est un panneau
     `position: absolute`. `overflow: hidden` le couperait — la liste s'ouvre et
     ne se voit pas. Un coin qui dépasse de six pixels se répare à l'œil ; une
     liste invisible ne se répare pas du tout. */
  return (
    <section style={{ ...card, padding: 0 }}>
      <button type="button" onClick={basculer} aria-expanded={ouvert} aria-controls={cle}
        style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', border: 'none', background: 'transparent', padding: '12px 14px', cursor: 'pointer', textAlign: 'start' }}>
        <I n={ouvert ? 'chevron-bas' : 'chevron-right'} s={14} />
        <span style={{ ...mono, fontWeight: 700, color: 'var(--color-primary-active)' }}>{num}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{titre}</span>
        {badgeEtat(etat)}
        <span style={{ flex: 1 }} />
        {!ouvert && resume && (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resume}</span>
        )}
      </button>
      {ouvert && <div id={cle} style={{ borderTop: '1px solid var(--border-subtle)' }}>{enfants}</div>}
    </section>
  );
}

/** Une valeur reprise de l'application : lisible, et disant d'où elle vient. */
function ValeurReprise({ label, valeur, source }: { label: string; valeur?: React.ReactNode; source?: string }) {
  const vide = valeur === null || valeur === undefined || valeur === '' || valeur === false;
  return (
    <div>
      <span style={lbl}>{label}</span>
      <span style={{ color: vide ? 'var(--text-muted)' : 'var(--text-strong)', fontSize: 12.5 }}>
        {vide ? '— non renseigné —' : valeur}
      </span>
      {!vide && source && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>repris de {source}</div>
      )}
    </div>
  );
}

const grilleFiche: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, padding: 14,
};

/** Les champs du §2, une seule fois. Ils étaient écrits deux fois — en lecture
 *  et en saisie — avec le risque qu'un champ ajouté n'apparaisse que d'un côté.
 *  `operateurs` n'y figure pas : il vit dans `miseEnOeuvre` (§3), pas dans la
 *  description de la méthode, et s'affiche à part. */
const CHAMPS_METHODE: [string, string][] = [
  ['mesurande', 'Mesurande'], ['matrice', 'Matrice'], ['unite', 'Unité'],
  // Seconde unité et facteur : SAISIS AU RÉFÉRENTIEL des paramètres, repris
  // ici. Le facteur dépend de la masse molaire — c'est une propriété du
  // paramètre, pas de la validation qui l'étudie.
  ['uniteSecondaire', 'Seconde unité'], ['facteurConversion', 'Facteur de conversion'],
  ['principe', 'Principe'],
  ['typeEchantillon', 'Type d’échantillon'], ['recipientAdditifs', 'Récipient / additifs'],
  ['equipement', 'Équipement'], ['referenceReactif', 'Référence réactif'],
  ['materiauEtalonnage', 'Matériau d’étalonnage'], ['typeEtalonnage', 'Type d’étalonnage'],
  ['criteresInterpretation', 'Critères d’interprétation'], ['marquageCE', 'Marquage CE'],
];

function Detail({ id, onRetour }: { id: string; onRetour: () => void }) {
  const { demander, confirmation } = useConfirmation();
  const { showToast, equips } = useGed();
  /* Pliage explicite de l'utilisateur, section par section. Une section absente
     de cet objet suit son défaut (ouverte si elle réclame quelque chose) : on
     distingue « jamais touché » de « replié exprès », sinon la première lecture
     rouvrirait ce que la RQ vient de replier. */
  const [plie, setPlie] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {};
    try { return JSON.parse(sessionStorage.getItem(`dvm-plie-${id}`) || '{}'); } catch { return {}; }
  });
  const retenir = (x: Record<string, boolean>) => {
    // Mode privé ou quota plein : le pliage ne survit pas au changement de page.
    // Ce n'est pas une erreur à remonter — c'est une position de lecture.
    try { sessionStorage.setItem(`dvm-plie-${id}`, JSON.stringify(x)); } catch { /* sans effet */ }
    return x;
  };
  const basculer = (num: string, defautOuvert: boolean) =>
    setPlie((x) => retenir({ ...x, [num]: !(x[num] === undefined ? !defautOuvert : x[num]) }));
  const toutes = (replier: boolean) =>
    setPlie(() => retenir(Object.fromEntries(NUMEROS_SECTIONS.map((n) => [n, replier]))));
  /** Une section suit son défaut tant que personne ne l'a pliée à la main. */
  const estOuverte = (num: string, defautOuvert: boolean) => (plie[num] === undefined ? defautOuvert : !plie[num]);

  const [d, setD] = useState<Dossier | null>(null);
  const [chargement, setChargement] = useState(true);
  const [tour, setTour] = useState(0);
  const [decision, setDecision] = useState('');
  const [motifRevision, setMotifRevision] = useState('');
  const [erreur, setErreur] = useState('');
  const [fiche, setFiche] = useState<Record<string, string> | null>(null);
  /* La fiche méthode LUE quand on clique « Modifier » (14/10/2026), figée jusqu'à l'enregistrement :
     un rechargement du dossier pendant la saisie ne la déplace pas. */
  const [ficheBase, setFicheBase] = useState<Record<string, unknown> | null>(null);
  const { run, estOccupe } = useBusy();

  useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/dvm/${id}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setD(j); })
      .catch(() => { if (vivant) setD(null); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [id, tour]);

  const recharger = () => setTour((t) => t + 1);

  const transition = (statut: string) => run(statut, async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/dvm/${id}/statut`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ statut, decision }),
    });
    const j = await r.json().catch(() => null);
    if (!r.ok) { setErreur(j?.error?.message || 'Transition refusée.'); return; }
    setDecision(''); recharger();
  });

  const enregistrerFiche = () => run('fiche', async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/ged/dvm/${id}`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        descriptionMethode: { ...(d?.dossier.descriptionMethode || {}), ...fiche }, miseEnOeuvre: { ...(d?.dossier.miseEnOeuvre || {}), operateurs: fiche?.operateurs || '' },
        ...(ficheBase ? { base: ficheBase } : {}),
      }),
    });
    if (!r.ok) { setErreur((await r.json().catch(() => null))?.error?.message || 'Enregistrement refusé.'); return; }
    /* Confirmation explicite (demande de la RQ, 14/08/2026). Un dossier de
       validation se remplit en plusieurs séances : sans retour visible, on ne
       sait pas si l'on peut fermer l'écran, et on ressaisit « pour être sûr ».
       Le message dit les DEUX choses qui lèvent le doute — c'est enregistré, et
       le dossier reste ouvert. */
    showToast('Enregistrement pris en compte — le dossier reste en cours, vous pourrez le reprendre plus tard.');
    setFiche(null); setFicheBase(null); recharger();
  });

  if (chargement) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement du dossier…</p>;
  if (!d) return null;
  const dd = d.dossier;
  const st = STATUTS[dd.statut] || STATUTS.brouillon;
  const peut = d.peutEcrire && !dd.fige;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Bouton taille="sm" onClick={onRetour} style={{ justifySelf: 'start' }}>
        <I n="chevron-left" s={13} />Tous les dossiers
      </Bouton>

      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ ...mono, fontWeight: 700, color: 'var(--color-primary-active)' }}>{dd.ref}</span>
        {/* Règle RQ du 18/08, câblée le 24/08/2026 : l'ouverture, les révisions
            et les décisions du dossier s'écrivaient au journal sans accès ici. */}
        <BoutonJournal reference={dd.ref} quoi="de ce dossier de validation" />
        <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-strong)' }}>{dd.libelleExamen || dd.analyteLibelle}</span>
        {pastille(st)}
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {dd.mot} de méthode · portée {dd.portee} · {dd.typeResultat} · version {dd.version}
          {dd.equipementCode ? ` · ${dd.equipementCode}` : ''}
        </span>
      </div>

      {dd.dvmPrecedent && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Révision du dossier <span style={mono}>{dd.dvmPrecedent}</span>, qui reste consultable et garde sa valeur de preuve.
        </p>
      )}

      {dd.fige && (
        <div style={{ background: 'var(--gray-50)', borderInlineStart: '3px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-strong)' }}>Ce dossier est figé.</strong> Mesures,
          statistiques, critères appliqués et version du référentiel ne se relisent plus : ils ont été
          copiés. Si la carte de contrôle reçoit un nouveau point demain, ce dossier ne bougera pas
          d’un chiffre. Toute évolution de la méthode passe par une <strong>révision chaînée</strong>.
          {dd.refSourceFigee && (
            <> Jugé contre <span style={mono}>{dd.refSourceFigee} — version {dd.refVersionFigee}</span>.</>
          )}
        </div>
      )}

      {/* ── Ce qui empêche de conclure. Visible en permanence : c'est la valeur
          d'usage la plus immédiatement perceptible par le biologiste. */}
      {d.blocages.length > 0 ? (
        <div style={{ background: 'var(--red-50)', borderInlineStart: '3px solid var(--red-500)', borderRadius: 'var(--radius-sm)', padding: '11px 14px' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', marginBottom: 6 }}>
            {d.blocages.length} élément{d.blocages.length > 1 ? 's' : ''} obligatoire{d.blocages.length > 1 ? 's' : ''} manque{d.blocages.length > 1 ? 'nt' : ''} — le dossier ne peut pas être déclaré apte.
          </div>
          <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.6 }}>
            {d.blocages.map((b, i) => (
              <li key={i}><strong>{b.libelle}</strong> — {b.motif}</li>
            ))}
          </ul>
        </div>
      ) : !dd.fige && (
        <div style={{ background: 'var(--green-50)', borderInlineStart: '3px solid var(--green-600)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          Tous les éléments obligatoires du gabarit sont renseignés. La déclaration d’aptitude reste
          un <strong>acte de personne</strong> : l’outil a calculé et comparé, il ne conclut pas.
        </div>
      )}

      {d.avertissements.map((a, i) => (
        <div key={i} style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>{a}</div>
      ))}

      {/* ── La barre de plan, comme le volet de navigation de Word ────────
          Deux commandes seulement : tout déplier pour relire d'un trait, tout
          replier pour retrouver le plan. Le reste se fait section par section. */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Le dossier suit le plan du gabarit — les sections ci-dessous sont celles du document imprimé.
        </span>
        <span style={{ flex: 1 }} />
        <Bouton taille="sm" type="button" onClick={() => toutes(false)}>Tout déplier</Bouton>
        <Bouton taille="sm" type="button" onClick={() => toutes(true)}>Tout replier</Bouton>
      </div>

      {/* ── §0 Identification ─────────────────────────────────────────────
          ENTIÈREMENT repris de l'application : rien ne s'y saisit. Ces valeurs
          ont été choisies à la création du dossier (catalogue, parc, discipline)
          et se corrigent là où elles vivent, pas ici — les recopier dans un
          formulaire ferait deux vérités pour un seul fait. */}
      <SectionDvm num="§0" titre="Identification" etat="repris"
        resume={`${dd.libelleExamen || dd.analyteLibelle} · ${dd.domaine || 'domaine non précisé'}`}
        ouvert={estOuverte('§0', false)} basculer={() => basculer('§0', false)}
        enfants={(
          <>
            <div style={grilleFiche}>
              <ValeurReprise label="Laboratoire" valeur="BIO24 Alliance" />
              <ValeurReprise label="Domaine" valeur={dd.domaine} source="la discipline du catalogue" />
              <ValeurReprise label="Sous-domaine" valeur={dd.sousDomaine} source="la discipline du catalogue" />
              <ValeurReprise label="Sous-domaine technique" valeur={dd.sousDomaineTechnique} source="la discipline du catalogue" />
              <ValeurReprise label="Identification de l’examen" valeur={dd.libelleExamen || dd.analyteLibelle} source="le catalogue des examens" />
              <ValeurReprise label={dd.parametres && dd.parametres.length > 1 ? 'Paramètres' : 'Code paramètre'}
                valeur={(dd.parametres || []).map((x) => x.code).join(', ') || dd.analyteCode}
                source="le référentiel des paramètres" />
              {/* La 4e dimension de la ligne de portée. Affichée même seule :
                  un dossier qui ne dit pas sur quelle matière il porte laisse
                  l'auditeur le déduire du récipient, deux chapitres plus loin. */}
              <ValeurReprise label={(dd.matrices?.length || 0) > 1 ? 'Matières biologiques' : 'Matière biologique'}
                valeur={(dd.matrices || []).map((m) => m.libelle).join(', ') || dd.descriptionMethode?.matrice}
                source={(dd.matrices?.length || 0) ? 'la fiche du catalogue' : undefined} />
              <ValeurReprise label="Équipement" valeur={[dd.equipementCode, dd.equipementLibelle].filter(Boolean).join(' — ')} source="le parc d’équipements" />
              <ValeurReprise label="Type de résultat" valeur={dd.typeResultat === 'qualitatif' ? 'Qualitatif' : 'Quantitatif'} />
              <ValeurReprise label="Portée"
                valeur={`${dd.portee} — méthode ${dd.portee === 'B' ? 'développée ou modifiée (validation)' : 'fournisseur adoptée sans modification (vérification)'}`} />
              <ValeurReprise label="Processus"
                valeur={dd.processusComplexe ? `Complexe — ${dd.nbSousProcessus || '?'} sous-processus` : 'Simple'} />
              <ValeurReprise label="Flexibilité choisie" valeur={dd.flexibilite} />
              <ValeurReprise label="Révision" valeur={`version ${dd.version}`} />
            </div>
            {/* Le cadre réglementaire : trois lignes fixes, mais fixes POUR DE
                BON — c'est ce que le dossier imprimé affirme en première page,
                et un auditeur le vérifie. Les recopier à la main dans chaque
                dossier serait le meilleur moyen d'en avoir treize versions. */}
            <div style={{ borderTop: '1px solid var(--border-subtle)', padding: 14, display: 'grid', gap: 6, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.55 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>Références</div>
              <div>Gabarit <strong>TUNAC ANNEXE 01 F.BM.20</strong> — rév. 02, février 2024. Une fiche par examen de biologie médicale.</div>
              <div>Norme <strong>NF EN ISO 15189:2022 §7.3.3</strong> — validation et vérification des méthodes d’examen.</div>
              <div>
                Critères d’acceptation :{' '}
                {d.referentiel
                  ? <><strong>{d.referentiel.sourceCode} version {d.referentiel.sourceVersion}</strong>, ligne « {d.referentiel.libelle} »{d.referentiel.referenceBiblio ? ` — ${d.referentiel.referenceBiblio}` : ''}.</>
                  : <span style={{ color: 'var(--amber-700)' }}>aucune ligne de référentiel ne correspond à ce paramètre : les essais resteront sans verdict.</span>}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                Référence interne du dossier <span style={mono}>{dd.ref}</span>.
              </div>
            </div>
          </>
        )} />

      {/* ── §1 Modalités retenues ─────────────────────────────────────────
          Déduit, jamais choisi : la note de bas de grille du gabarit impose les
          éléments selon le type de résultat et la portée. Cette section n'est
          pas un formulaire — c'est le tableau que l'auditeur lit en premier
          pour savoir ce qui devait être fait. */}
      <SectionDvm num="§1" titre={`Description des modalités de ${dd.mot}`} etat="repris"
        resume={`${d.grille.length} modalités · ${d.grille.filter((m) => m.essai).length} renseignées`}
        ouvert={estOuverte('§1', false)} basculer={() => basculer('§1', false)}
        enfants={(
          <div style={{ padding: 14, display: 'grid', gap: 10 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Cette liste n’est pas un choix : elle découle du type de résultat
              ({dd.typeResultat === 'qualitatif' ? 'qualitatif' : 'quantitatif'}) et de la portée {dd.portee}.
              Le détail de chaque modalité se remplit au §5.
            </p>
            {/* L7 vague 2 (11/10/2026) : pièce de dossier rendue par le socle au
                niveau SIMPLE — l'ordre du gabarit reste l'ordre par défaut. */}
            <Tableau
              id="dvm-modalites"
              ajusterLargeur
              niveau="simple"
              lignes={d.grille}
              cleLigne={(m) => m.type}
              colonnes={[
                { cle: 'element', libelle: 'Élément', valeur: (m) => m.element, style: mono, rendu: (m) => m.element },
                { cle: 'libelle', libelle: 'Modalité', valeur: (m) => m.libelle, style: { color: 'var(--text-strong)' }, rendu: (m) => m.libelle },
                { cle: 'exigence', libelle: 'Exigence', type: 'liste', valeur: (m) => m.exigence, libelleValeur: libelleExigence, rendu: (m) => libelleExigence(m.exigence) },
                {
                  cle: 'etat', libelle: 'État', type: 'liste', valeur: (m) => etatModalite(m),
                  libelleValeur: (v) => LIBELLE_ETAT_MODALITE[v as keyof typeof LIBELLE_ETAT_MODALITE] || v,
                  rendu: (m) => (etatModalite(m) === 'a_renseigner'
                    ? <span style={{ color: 'var(--amber-700)' }}>à renseigner</span>
                    : LIBELLE_ETAT_MODALITE[etatModalite(m)]),
                },
                {
                  cle: 'verification', libelle: 'Vérification', style: { color: 'var(--text-muted)' },
                  valeur: (m) => (!m.essai || m.essai.applicable === false ? '' : (m.essai.typeVerification || 'non précisée')),
                  rendu: (m) => (!m.essai || m.essai.applicable === false ? '—' : (m.essai.typeVerification || 'non précisée')),
                },
              ]}
            />
          </div>
        )} />

      {/* ── §2 Description de la méthode ─────────────────────────────────
          La seule section du haut qui se SAISIT — et encore : l'essentiel y
          arrive pré-rempli du catalogue, du parc et du référentiel, avec sa
          provenance écrite dessous. L'appareil miroir y est rattaché plutôt
          que posé à part : il décrit la méthode, pas le dossier. */}
      <SectionDvm num="§2" titre="Description de la méthode"
        etat={CHAMPS_METHODE.every(([k]) => (dd.descriptionMethode?.[k] || '').trim()) ? 'complet' : 'a_completer'}
        resume={dd.descriptionMethode?.principe || dd.descriptionMethode?.mesurande}
        ouvert={estOuverte('§2', true)} basculer={() => basculer('§2', true)}
        enfants={(
      <div style={{ padding: 14, display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ flex: 1 }} />
          {/* Ne remplit QUE le vide, et le dit. Un bouton qui pourrait écraser
              une saisie est un bouton qu'on n'ose plus presser. */}
          {peut && !fiche && !dd.fige && (
            <Bouton taille="sm" title="Reprend du catalogue, du parc et de la dernière calibration les champs encore vides. Rien de ce qui est écrit n’est touché." onClick={() => void run('reprise', async () => {
                setErreur('');
                const r = await fetchEcriture(`/api/ged/ged/dvm/${id}/reprendre-app`, {
                  method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}',
                });
                const j = await r.json().catch(() => null);
                if (!r.ok) { setErreur(j?.error?.message || 'Reprise refusée.'); return; }
                showToast(j?.remplis?.length
                  ? `${j.remplis.length} champ(s) repris de l’application.`
                  : 'Rien à reprendre : tout ce que l’application sait est déjà dans le dossier.');
                recharger();
              })} disabled={estOccupe()}>
              <I n="refresh" s={13} />Reprendre de l’application
            </Bouton>
          )}
          {peut && !fiche && (
            <Bouton taille="sm" onClick={() => {
              setFiche({ ...dd.descriptionMethode, operateurs: dd.miseEnOeuvre?.operateurs || '' });
              setFicheBase({ descriptionMethode: { ...(dd.descriptionMethode || {}) }, miseEnOeuvre: { ...(dd.miseEnOeuvre || {}) } });
            }}><I n="pen" s={13} />Modifier</Bouton>
          )}
        </div>

        {/* Provenance de chaque valeur reprise — et, quand une source n'a rien
            donné, POURQUOI. Un champ vide sans explication se lit comme un oubli
            de saisie ; ici il signale souvent un dispositif que le laboratoire
            n'a pas encore établi (aucun intervalle de référence adopté, aucune
            carte de contrôle pour ce mesurande). Les deux ne se corrigent pas au
            même endroit, et ne pas le dire ferait chercher au mauvais. */}
        {Object.entries(dd.prefill || {}).filter(([, v]) => v).map(([k, v]) => (
          v!.absent ? (
            <div key={k} style={{ fontSize: 12.5, color: 'var(--amber-700)', lineHeight: 1.5, background: 'var(--amber-50)', borderRadius: 'var(--radius-sm)', padding: '7px 10px' }}>
              {v!.absent}
            </div>
          ) : (
            <div key={k} style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Pré-rempli depuis <strong>{v!.source}</strong> :{' '}
              {Object.entries(v!.valeurs)
                .filter(([, x]) => x !== null && x !== undefined && x !== '' && !(Array.isArray(x) && !x.length))
                .map(([ck, cv]) => `${ck} ${Array.isArray(cv) ? cv.join(', ') : String(cv)}`)
                .join(' · ') || '—'}
            </div>
          )
        ))}

        {!fiche ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, fontSize: 12.5 }}>
            {CHAMPS_METHODE.map(([k, label]) => (
              <div key={k}>
                <span style={lbl}>{label}</span>
                <span style={{ color: dd.descriptionMethode?.[k] ? 'var(--text-strong)' : 'var(--text-muted)' }}>
                  {dd.descriptionMethode?.[k] || '— non renseigné —'}
                </span>
              </div>
            ))}
            <div>
              <span style={lbl}>Opérateurs qualifiés (§3)</span>
              <span style={{ color: dd.miseEnOeuvre?.operateurs ? 'var(--text-strong)' : 'var(--text-muted)' }}>
                {dd.miseEnOeuvre?.operateurs || '— non renseigné —'}
              </span>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
              {[...CHAMPS_METHODE, ['operateurs', 'Opérateurs qualifiés (§3)'] as [string, string]].map(([k, label]) => (
                <div key={k}>
                  <label style={lbl} htmlFor={`fm-${k}`}>{label}</label>
                  <input id={`fm-${k}`} style={inputStyle} value={fiche[k] || ''}
                    onChange={(ev) => setFiche((x) => ({ ...(x || {}), [k]: ev.target.value }))} />
                </div>
              ))}
            </div>
            <PiedActions taille="sm" annuler={{ onClick: () => setFiche(null) }} principale={{ libelle: "Enregistrer", disabled: estOccupe(), onClick: () => void enregistrerFiche() }} />
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, marginTop: 2 }} />
      {/* ── APPAREIL MIROIR ────────────────────────────────────────────────
            Toujours un COUPLE (décision RQ du 14/08/2026). Deux appareils
            identiques qui rendent le même examen — les Indiko 1 et 2, les deux
            ABL80 — doivent démontrer qu'ils rendent la MÊME chose : c'est ce que
            le §7.3.3 attend dès qu'un examen est porté par plus d'un instrument.
            Sans ce champ, le dossier ne décrivait qu'un appareil et la
            comparabilité restait hors dossier. */}
        <div style={{ ...card, padding: 14, display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Appareil miroir</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              second automate rendant le même examen — comparabilité §7.3.3
            </span>
          </div>
          {dd.equipementMiroirCode ? (
            <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
              <strong>{dd.equipementMiroirLibelle}</strong>{' '}
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>({dd.equipementMiroirCode})</span>
              {' '}— comparé à <strong>{dd.equipementLibelle}</strong>{' '}
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>({dd.equipementCode})</span>.
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Aucun appareil miroir désigné. Ce n’est un manque QUE si cet examen est réellement
              rendu par un second instrument — beaucoup ne le sont que par un seul.
            </div>
          )}
          {peut && !dd.fige && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Selecteur value={dd.equipementMiroirCode || ''} aria-label="Appareil miroir"
                onChange={(v) => void run('miroir', async () => {
                  const r = await fetchEcriture(`/api/ged/ged/dvm/${id}`, {
                    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ equipementMiroirCode: v }),
                  });
                  if (!r.ok) { setErreur((await r.json().catch(() => null))?.error?.message || 'Enregistrement refusé.'); return; }
                  showToast('Enregistrement pris en compte — le dossier reste en cours, vous pourrez le reprendre plus tard.');
                  recharger();
                })}
                style={{ minWidth: 260 }}>
                <option value="">— aucun appareil miroir —</option>
                {/* Sans accessoires, et sans l'appareil du dossier : se comparer à
                    soi-même ne démontre rien. Le serveur refuse aussi. */}
                {equipementsDesignables(equips)
                  .filter((e) => e.code && e.code !== dd.equipementCode)
                  .map((e) => <option key={e.documentId} value={e.code as string}>{e.code} — {e.nom}</option>)}
              </Selecteur>
            </div>
          )}
        </div>
      </div>
        )} />

      {/* ── §3 Mise en œuvre ───────────────────────────────────────────────
          La liste vient des HABILITATIONS, à l'instant où la page se charge.
          Elle n'est pas recopiable : un champ libre en ferait une photographie
          qui vieillirait sans que personne ne s'en aperçoive — et c'est une
          pièce d'accréditation. Le commentaire du §2 reste, lui, à la main du
          biologiste : il dit ce que la matrice ne sait pas (« deux opérateurs
          formés au réactif du nouveau lot »). */}
      <SectionDvm num="§3" titre="Mise en œuvre — opérateurs qualifiés"
        etat={dd.operateursHabilites?.some((o) => o.niveau === 'habilite') ? 'repris' : 'a_completer'}
        resume={(dd.operateursHabilites || []).filter((o) => o.niveau === 'habilite').length
          ? `${dd.operateursHabilites.filter((o) => o.niveau === 'habilite').length} opérateur(s) habilité(s) en ${dd.sousDomaine}`
          : 'aucun opérateur habilité sur cette discipline'}
        ouvert={estOuverte('§3', false)} basculer={() => basculer('§3', false)}
        enfants={(
          <div style={{ padding: 14, display: 'grid', gap: 10 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Repris des habilitations sur la discipline <strong>{dd.sousDomaine || '— non précisée —'}</strong>,
              au moment où cette page se charge. Pour en modifier une, passez par le dossier de la
              personne : cette liste n’est pas saisissable ici, sans quoi le dossier et les
              habilitations diraient deux choses différentes.
            </p>
            {(dd.operateursHabilites || []).length ? (
              <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.7 }}>
                {dd.operateursHabilites.map((o) => (
                  <li key={o.nom}>
                    <strong style={{ color: 'var(--text-strong)' }}>{o.nom}</strong>
                    {o.niveau === 'habilite'
                      ? ' — habilité'
                      : <span style={{ color: 'var(--amber-700)' }}> — habilitation en cours, ne compte pas comme opérateur qualifié</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ fontSize: 12.5, color: 'var(--amber-700)', background: 'var(--amber-50)', borderRadius: 'var(--radius-sm)', padding: '9px 12px', lineHeight: 1.5 }}>
                Aucune habilitation n’est enregistrée sur cette discipline. Un dossier de vérification
                qui ne nomme personne laisse ouverte la question du §6.2 : qui a le droit de rendre ce résultat ?
              </div>
            )}
          </div>
        )} />

      <Rubriques dvmId={id} dd={dd} peut={peut} onFait={recharger}
        ouvert={estOuverte('§4·6·10', true)} basculer={() => basculer('§4·6·10', true)} />

      {/* ── §5 La grille des modalités ─────────────────────────────────────
          Le corps du dossier. Il reste ouvert par défaut tant qu'une modalité
          obligatoire attend : c'est là que se passe le travail. */}
      <SectionDvm num="§5" titre="Évaluation des performances"
        etat={d.grille.every((m) => m.essai) ? 'complet' : 'a_completer'}
        resume={`${d.grille.filter((m) => m.essai).length}/${d.grille.length} modalités renseignées`}
        ouvert={estOuverte('§5', true)} basculer={() => basculer('§5', true)}
        enfants={(
      <div>
        <div style={{ padding: '11px 14px' }}>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            La liste ci-dessous n’est pas un choix : elle vient de la note de bas de grille du
            formulaire, qui impose les éléments {dd.typeResultat === 'qualitatif' ? '3, 6, 8, 9, 10, 11, 12, 13' : '1, 2, 4, 5, 7, 8, 9, 10, 11, 12, 13'} pour
            une méthode {dd.typeResultat === 'qualitatif' ? 'qualitative' : 'quantitative'}
            {dd.portee === 'B' && ', et l’étude expérimentale pour l’étendue de mesure, la robustesse et les intervalles de référence en portée B'}.
            {d.referentiel
              ? <> Les seuils viennent du référentiel <strong>{d.referentiel.sourceCode} version {d.referentiel.sourceVersion}</strong>, ligne « {d.referentiel.libelle} ».</>
              : <> <strong>Aucune ligne de référentiel ne correspond à ce paramètre</strong> : tous les essais resteront sans verdict tant qu’un critère n’aura pas été rattaché.</>}
          </p>
        </div>
        <Performances d={d} dd={dd} id={id} recharger={recharger} />
      </div>
        )} />

      {/* ── §8 Déclaration d'aptitude ──────────────────────────────────────
          Les actes déjà signés et la décision à prendre ne font qu'une section :
          séparés, on pouvait signer sans voir ce qui l'avait déjà été. */}
      {(d.signatures.length > 0 || peut) && (
      <SectionDvm num="§8" titre="Déclaration d’aptitude"
        etat={d.signatures.length ? 'complet' : 'a_completer'}
        resume={d.signatures.length ? `${d.signatures.length} acte(s) signé(s)` : 'aucune décision signée'}
        ouvert={estOuverte('§8', !d.signatures.length)} basculer={() => basculer('§8', !d.signatures.length)}
        enfants={(<div style={{ padding: 14, display: 'grid', gap: 12 }}>
      {d.signatures.length > 0 && (
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', marginBottom: 8 }}>Actes signés</div>
          {d.signatures.map((s) => (
            <div key={s.documentId} style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.6, paddingBlock: 4, borderTop: '1px solid var(--border-subtle)' }}>
              <strong style={{ color: 'var(--text-strong)' }}>{s.nature === 'aptitude' ? 'Déclaration d’aptitude' : s.nature === 'evolution' ? 'Évolution de la méthode' : s.nature}</strong>
              {' — '}{s.signataireNom}, {fmtDate(s.dateSignature)}
              <div style={{ color: 'var(--text-muted)' }}>{s.decision}</div>
            </div>
          ))}
        </div>
      )}

      {erreur && (
        <div style={{ background: 'var(--red-50)', borderInlineStart: '3px solid var(--red-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>{erreur}</div>
      )}

      {peut && (
        <div style={{ display: 'grid', gap: 10 }}>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Écrivez la décision. Elle sera signée à votre nom et déposée au journal chaîné —
            l’outil ne conclut pas à la place du biologiste.
          </p>
          <textarea rows={3} style={inputStyle} value={decision} onChange={(e) => setDecision(e.target.value)}
            aria-label="Décision d’aptitude"
            placeholder="Ex. : la méthode répond aux critères de performance retenus sur les deux niveaux de contrôle ; mise en service autorisée." />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {dd.statut !== 'a_approuver' && (
              <Bouton taille="sm" onClick={() => void transition('a_approuver')} disabled={estOccupe()}>
                Soumettre à approbation
              </Bouton>
            )}
            <Bouton variante="primaire" onClick={() => void transition('apte')} disabled={estOccupe() || !d.peutConclure} title={d.peutConclure ? undefined : 'Des éléments obligatoires manquent — la liste est au-dessus.'}>
              <I n="check" s={14} />Déclarer apte
            </Bouton>
            <Bouton variante="danger" taille="sm" onClick={() => void transition('inapte')} disabled={estOccupe()}>
              Déclarer inapte
            </Bouton>
          </div>
        </div>
      )}

      </div>)} />
      )}

      {/* Repliée par défaut : sur un dossier figé, ouvrir une révision est un
          acte rare, et une zone de saisie ouverte en bas de page invite à s'en
          servir. */}
      {d.peutEcrire && dd.fige && dd.statut !== 'obsolete' && (
      <SectionDvm num="§11" titre="Évolution de la méthode" etat="complet"
        resume="ouvrir un dossier successeur, chaîné à celui-ci"
        ouvert={estOuverte('§11', false)} basculer={() => basculer('§11', false)}
        enfants={(
        <div style={{ padding: 14, display: 'grid', gap: 10 }}>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Un dossier signé ne se réécrit pas : on en ouvre le successeur, chaîné au précédent.
            L’ancien passe en « obsolète » et garde tout ce qu’il disait.
          </p>
          <textarea rows={2} style={inputStyle} value={motifRevision} onChange={(e) => setMotifRevision(e.target.value)}
            aria-label="Nature de l’évolution" placeholder="Nature de l’évolution : changement de lot de réactif, de calibrateur, d’automate…" />
          <div>
            <Bouton taille="sm" onClick={() => void run('rev', async () => {
              setErreur('');
              const r = await fetchEcriture(`/api/ged/ged/dvm/${id}/reviser`, {
                method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ motif: motifRevision }),
              });
              const j = await r.json().catch(() => null);
              if (!r.ok) { setErreur(j?.error?.message || 'Révision refusée.'); return; }
              onRetour();
            })} disabled={estOccupe()}>
              Ouvrir une révision
            </Bouton>
          </div>
        </div>
        )} />
      )}

      {/* ── RETIRER UN DOSSIER ENCORE EN RÉDACTION (RQ, 26/08/2026) ────────
          « Tant que le dossier est en cours de rédaction et non encore transmis
          au GED publié, me laisser la main pour le supprimer totalement. »

          Un dossier ouvert sur le mauvais examen, la mauvaise portée ou le
          mauvais automate ne se corrige pas : la grille des modalités est
          dépliée à la création. Sans suppression, il reste au registre pour
          toujours — et des brouillons morts font douter de ceux qui comptent.

          Le bouton disparaît dès que le dossier est figé : le serveur refuse
          aussi, mais un bouton qui ne mène qu'à un refus est un piège. */}
      {peut && !dd.fige && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Bouton variante="danger" taille="sm" onClick={() => void run('suppr', async () => {
              setErreur('');
              const combien = d.grille.filter((m) => m.essai).length;
              if (!(await demander(
                `Supprimer définitivement ce dossier ?\n\n${dd.ref} — ${dd.libelleExamen || dd.analyteLibelle}\n`
                + `${combien} modalité(s) renseignée(s)${dd.docCode ? `\nLe brouillon documentaire ${dd.docCode} part avec lui.` : ''}\n\n`
                + 'Le retrait est écrit au journal ; le dossier, lui, ne se récupère pas.',
                { titre: 'Supprimer le dossier', confirmer: 'Supprimer', danger: true },
              ))) return;
              const r = await fetchEcriture(`/api/ged/ged/dvm/${id}`, { method: 'DELETE', credentials: 'include' });
              if (!r.ok) { setErreur((await r.json().catch(() => null))?.error?.message || 'Suppression refusée.'); return; }
              showToast('Dossier supprimé — le retrait est au journal.');
              onRetour();
            })} disabled={estOccupe()}>
            <I n="trash" s={13} />Supprimer ce dossier
          </Bouton>
        </div>
      )}

      {/* ── L'ATELIER DU DOCUMENT, EN FIN DE PLAN ──────────────────────────
          Ce n'est pas un chapitre du gabarit : c'est d'où sort le Word et où il
          se dépose à la GED. Placé au milieu, il coupait la lecture entre le §5
          et la déclaration d'aptitude — un lecteur qui suit le plan du document
          n'attend pas un outil d'export au beau milieu de ses chapitres. */}
      <DansLaGed dvmId={id} dd={dd} chapitres={d.chapitres} reserves={d.reserves} onFait={recharger} />
      {confirmation}
    </div>
  );
}

/* ==========================================================================
 *  Ouverture d'un dossier
 * ======================================================================== */

/** Ce que le référentiel sait d'un paramètre : ses techniques et les appareils
 *  qui les portent. Rendu par `GET ged/dvm/options/:code`. */
type OptionsParametre = {
  analyte: { code: string; nom: string; typeResultat: string | null };
  techniques: { code: string; nom: string; principe: string; equipements: { code: string; nom: string }[] }[];
  equipements: { code: string; nom: string }[];
  comparaisonProposee: boolean;
};

type MatriceCat = { code: string; echantillon: string; recipient: string | null; deduite: boolean };
type ExamenCat = {
  code: string; libelle: string; archivee?: boolean;
  analytes: { code: string; nom: string }[];
  /** Les matières biologiques, calculées par le serveur. L'écran ne refait pas
   *  le découpage des champs texte historiques : deux lectures d'un même champ
   *  finissent toujours par diverger d'une matière. */
  matrices?: MatriceCat[];
};

function Ouvrir({ onFait, onAnnule }: { onFait: (id: string) => void; onAnnule: () => void }) {
  const { analytes, equips } = useGed();
  /* ── ON PART DE L'EXAMEN, PAS DU PARAMÈTRE (RQ, 25/08/2026) ────────────────
   * « Quand je crée un dossier, je veux que ce soit la liste des examens et pas
   * la liste des paramètres qui apparaisse au départ. »
   *
   * C'est le bon ordre : le gabarit impose UNE FICHE PAR EXAMEN, et c'est
   * l'examen qui est accrédité. Partir du paramètre demandait au biologiste de
   * refaire mentalement la composition de l'examen à chaque dossier, avec le
   * risque d'en oublier un. Ici, choisir l'examen CHARGE ses paramètres : la
   * composition vient du référentiel. Elle reste modifiable, parce que le
   * référentiel ne compose pas encore tout le catalogue. */
  const [examens, setExamens] = useState<ExamenCat[]>([]);
  const [examenCode, setExamenCode] = useState('');
  const [libelleExamen, setLibelleExamen] = useState('');
  const [opts, setOpts] = useState<Record<string, OptionsParametre>>({});
  const [miroir, setMiroir] = useState('');
  /* ── LES MATIÈRES COUVERTES (RQ, 26/08/2026) ───────────────────────────────
   * La quatrième dimension de la ligne de portée d'accréditation : examen ×
   * MATIÈRE × technique × automate. Le dossier portait déjà les trois autres.
   *
   * Elles viennent de la fiche du catalogue et se DÉCOCHENT ; elles ne se
   * saisissent pas. Un dossier ne peut pas valider une matière que le manuel ne
   * promet pas — ce serait une ligne de portée sans examen derrière. */
  const [matrices, setMatrices] = useState<string[]>([]);

  /* ── UNE SEULE LISTE DE PARAMÈTRES, PAS « UN PRINCIPAL ET LES AUTRES » ─────
   * Deuxième retour de la RQ le 25/08 : « je n'aime pas la formulation
   * paramètre principal et autres paramètres couverts. Tous les paramètres
   * doivent se présenter les uns derrière les autres avec un + à la fin. »
   *
   * Elle a raison sur le fond : aucun paramètre d'un examen n'est plus
   * important qu'un autre — l'accréditation porte sur l'examen. Le « premier »
   * n'était qu'un détail de stockage (`analyteCode`, le champ d'origine), et
   * un détail de stockage n'a rien à faire dans un intitulé d'écran.
   *
   * ⚠️ Le stockage, lui, ne change pas : `lignes[0]` reste `analyteCode`. Un
   * dossier à un paramètre garde exactement la forme qu'il avait, et rien
   * d'existant n'est à reprendre. Retirer la première ligne promeut la
   * suivante — d'où le passage par un tableau plutôt que par deux états. */
  type Ligne = { code: string; techniqueCode: string; equipementCode: string };
  const [lignes, setLignes] = useState<Ligne[]>([]);
  const [presentation, setPresentation] = useState<'tableau' | 'sections'>('tableau');
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  const poser = (i: number, champ: keyof Ligne, v: string) =>
    setLignes((l) => l.map((x, j) => (j === i ? { ...x, [champ]: v } : x)));

  // Le catalogue, une fois. Il sert la liste de départ ET la composition de
  // chaque examen — deux requêtes pour la même donnée finiraient par diverger.
  useEffect(() => {
    let vivant = true;
    void (async () => {
      const r = await fetch('/api/ged/ged/manuel-ref/catalogue', { credentials: 'include' });
      const j = await r.json().catch(() => null);
      if (vivant && j?.fiches) setExamens((j.fiches as ExamenCat[]).filter((x) => !x.archivee));
    })();
    return () => { vivant = false; };
  }, []);

  /** Ce que le référentiel sait d'un paramètre, mis en cache : revenir sur un
   *  examen déjà vu ne doit pas refaire les requêtes. */
  const chargerOptions = async (code: string): Promise<OptionsParametre | null> => {
    if (!code) return null;
    const r = await fetch(`/api/ged/ged/dvm/options/${encodeURIComponent(code)}`, { credentials: 'include' });
    const j = await r.json().catch(() => null);
    if (!r.ok || !j) return null;
    setOpts((x) => ({ ...x, [code]: j as OptionsParametre }));
    return j as OptionsParametre;
  };

  /**
   * CHOISIR UN EXAMEN CHARGE SA COMPOSITION, une ligne par paramètre.
   *
   * ⚠️ L'équipement n'est préselectionné QUE s'il n'y en a qu'un. Avec deux
   * appareils — les deux Indiko — en choisir un au hasard ferait signer un
   * dossier sur un automate que personne n'a désigné, et ferait disparaître la
   * question de la comparaison inter-machines au lieu de la poser.
   */
  const choisirExamen = (code: string) => void run('examen', async () => {
    setExamenCode(code);
    setMiroir('');
    const ex = examens.find((x) => x.code === code);
    setLibelleExamen(ex?.libelle || '');
    setMatrices((ex?.matrices || []).map((m) => m.code));
    const params = (ex?.analytes || []).map((a) => a.code).filter(Boolean);
    if (!params.length) { setLignes([]); return; }

    const chargees = await Promise.all(params.map((c) => chargerOptions(c)));
    setLignes(params.map((c, i) => ({
      code: c,
      techniqueCode: chargees[i]?.techniques.length === 1 ? chargees[i]!.techniques[0].code : '',
      equipementCode: chargees[i]?.equipements.length === 1 ? chargees[i]!.equipements[0].code : '',
    })));
    // Plusieurs paramètres : les sections évitent de laisser croire à une
    // méthode commune quand les techniques diffèrent.
    if (params.length > 1) setPresentation('sections');
  });

  const [f, setF] = useState({ typeResultat: 'quantitatif', portee: 'A' });
  const premiere = lignes[0] || null;
  const optsPremiere = premiere ? opts[premiere.code] : undefined;

  /* ── LA NATURE VIENT DU RÉFÉRENTIEL (RQ, 25/08/2026) ───────────────────────
   * « La nature qualitatif ou quantitative doit être existante dans le
   * paramètre lui-même, et le DVM respecte les données du référentiel des
   * paramètres. » Elle y est : `ged-analyte.typeResultat`.
   *
   * Dès que le référentiel tranche, le champ n'est plus un choix : il affiche
   * ce que le référentiel dit et se corrige AU RÉFÉRENTIEL. Le laisser
   * modifiable ferait de l'écran une seconde vérité, et un clic distrait
   * ouvrirait un dossier quantitatif sur un anticorps qualitatif — onze
   * modalités dépliées au lieu de huit, sur la mauvaise grille.
   *
   * Il redevient un choix quand le référentiel ne peut PAS trancher : nature
   * absente, `semiquantitatif`/`texte` (que le gabarit ne distingue pas), ou
   * paramètres de natures opposées. Le calcul est le même que celui du serveur
   * — s'ils divergeaient, l'écran proposerait ce que le serveur refuse. */
  const naturesLues = lignes.map((x) => opts[x.code]?.analyte?.typeResultat || '').filter(Boolean);
  const distinctes = [...new Set(naturesLues)];
  const horsGabarit = distinctes.filter((x) => x !== 'quantitatif' && x !== 'qualitatif');
  const natureReferentiel = !distinctes.length || horsGabarit.length || distinctes.length > 1 ? null : distinctes[0];
  const motifNature = natureReferentiel ? ''
    : !distinctes.length ? 'Le référentiel ne dit pas la nature du résultat de ces paramètres — à préciser ici, et à compléter au référentiel.'
      : horsGabarit.length ? `Le référentiel déclare « ${horsGabarit.join(', ')} », que le gabarit TUNAC ne distingue pas : il n’a que deux colonnes. À vous de dire sur laquelle ce dossier est évalué.`
        : `Les paramètres n’ont pas tous la même nature au référentiel (${distinctes.join(' et ')}) : un même dossier ne peut pas être évalué sur les deux grilles. Retirez ceux qui n’ont rien à y faire, ou tranchez.`;

  /* La nature retenue est celle du référentiel quand il tranche : elle n'est
     pas rangée dans `f`, pour qu'aucun rendu ne puisse afficher une valeur que
     le serveur refuserait. */
  const typeResultat = natureReferentiel || f.typeResultat;

  const creer = () => run('save', async () => {
    setErreur('');
    if (!premiere?.code) { setErreur('Aucun paramètre : un dossier de validation porte sur au moins un mesurande.'); return; }
    const commun = {
      ...f,
      typeResultat,
      analyteCode: premiere.code,
      equipementCode: premiere.equipementCode,
      /* Le LIBELLÉ de l'examen voyage avec le dossier : sans lui, un dossier à
         plusieurs paramètres s'intitulerait du nom du premier — « Dossier de
         vérification — ALAT » pour un bilan hépatique. */
      ...(libelleExamen ? { libelleExamen } : {}),
      ...(miroir ? { equipementMiroirCode: miroir } : {}),
      ...(matrices.length ? { matrices } : {}),
    };
    /* `parametres` n'est envoyé QUE s'il y en a plusieurs : un dossier à un
       paramètre n'a pas à porter une liste d'un élément, qui ferait croire à
       une structure qu'il n'a pas. */
    const corps = lignes.length > 1
      ? {
        ...commun,
        presentation,
        parametres: lignes.map((x, i) => ({
          code: x.code,
          techniqueCode: x.techniqueCode || null,
          equipementCode: x.equipementCode || premiere.equipementCode || null,
          ordre: i + 1,
        })),
      }
      : commun;
    const r = await fetchEcriture('/api/ged/ged/dvm', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps),
    });
    const j = await r.json().catch(() => null);
    if (!r.ok) { setErreur(j?.error?.message || 'Création refusée.'); return; }
    onFait(j.documentId);
  });

  /** Une cellule d'en-tête du tableau des paramètres. */
  const th = (texte: string, largeur?: number) => (
    <th style={{ textAlign: 'start', padding: '6px 8px', width: largeur, borderBottom: '1px solid var(--border-default)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', fontWeight: 700 }}>
      {texte}
    </th>
  );
  const td: React.CSSProperties = { padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' };

  return (
    <div style={{ ...card, padding: 16, display: 'grid', gap: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Ouvrir un dossier</div>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
        Le formulaire impose <strong>une fiche par examen de biologie médicale</strong>. Un examen
        peut reposer sur <strong>plusieurs paramètres</strong>, y compris mesurés par des techniques
        différentes : ils tiennent dans le même dossier, puisque c’est l’examen qui est accrédité.
        Le type de résultat et la portée décident ensuite des essais exigés — vous ne les choisirez
        pas, l’assistant les impose.
      </p>

      {/* ── L'EXAMEN D'ABORD ───────────────────────────────────────────────
          Le choix qui commande tous les autres : il fixe le titre du dossier et
          charge sa composition. */}
      <div>
        <span style={lbl}>Examen</span>
        <Selecteur value={examenCode} onChange={choisirExamen} aria-label="Examen du catalogue">
          <option value="">{examens.length ? '— choisir un examen —' : 'chargement du catalogue…'}</option>
          {examens.map((x) => (
            <option key={x.code} value={x.code}>
              {x.code} — {x.libelle}{x.analytes?.length > 1 ? ` (${x.analytes.length} paramètres)` : ''}
            </option>
          ))}
        </Selecteur>
        {examenCode && !lignes.length && !estOccupe('examen') && (
          <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--amber-700)', lineHeight: 1.5 }}>
            Cet examen n’a aucun paramètre rattaché au référentiel : ajoutez-les à la main ci-dessous.
            La composition se corrige à l’écran « Référentiel ».
          </p>
        )}
      </div>

      {/* ── LES PARAMÈTRES, LES UNS SOUS LES AUTRES ────────────────────────
          Un tableau et pas deux blocs : chaque paramètre a les mêmes colonnes,
          les mêmes gestes, le même poids. Le « + » ajoute une ligne vide dont
          le paramètre se choisit dans la ligne — pas dans un formulaire à part,
          qui ferait de l'ajout un acte d'une autre nature. */}
      <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
        <div style={{ padding: '9px 12px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-subtle)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          Paramètres couverts par ce dossier
          {!!examenCode && !!lignes.length && (
            <span style={{ fontWeight: 400, fontSize: 12.5, color: 'var(--text-muted)' }}>
              {' '}— chargés depuis la composition de l’examen ; retirez ceux que ce dossier ne couvre pas.
            </span>
          )}
        </div>

        {/* ⚠️ PAS d'`overflow` ICI, ET C'EST LE POINT (incident du 25/08/2026).
            Chaque cellule porte un `Selecteur`, dont la liste est un panneau
            `position: absolute`. Un conteneur `overflow-x: auto` CLIPPE ce
            panneau — la règle CSS veut qu'un axe en `auto` empêche l'autre
            d'être `visible`. Résultat observé : la liste des paramètres et
            celle des équipements s'ouvraient hors champ, invisibles. Aucune
            erreur, aucun message ; la trace serveur montrait les options
            chargées puis PLUS RIEN — le dossier ne pouvait pas être ouvert et
            personne ne pouvait dire pourquoi.

            Le tableau se resserre donc plutôt que de défiler : quatre colonnes
            de listes tiennent, et une liste utilisable vaut mieux qu'une
            largeur confortable. */}
        <div>
          <table className="ged-tableau" ref={enTeteCollant} style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5, tableLayout: 'fixed' }}>
            <thead>
              <tr>
                {th('Paramètre')}
                {th('Technique')}
                {th('Équipement')}
                {th('', 44)}
              </tr>
            </thead>
            <tbody>
              {!lignes.length && (
                <tr>
                  <td colSpan={4} style={{ ...td, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    Aucun paramètre. Choisissez un examen ci-dessus, ou ajoutez-en un à la main.
                  </td>
                </tr>
              )}
              {lignes.map((x, i) => (
                <tr key={`${x.code}-${i}`}>
                  <td style={td}>
                    <Selecteur value={x.code} aria-label={`Paramètre ${i + 1}`}
                      onChange={(v) => { poser(i, 'code', v); poser(i, 'techniqueCode', ''); poser(i, 'equipementCode', ''); setMiroir(''); void chargerOptions(v); }}>
                      <option value="">— choisir —</option>
                      {/* Les paramètres déjà pris ne sont plus proposés : deux
                          lignes sur le même mesurande donneraient deux fois les
                          mêmes essais dans le document, et l'un des deux
                          resterait vide sans qu'on sache lequel compte. */}
                      {analytes
                        .filter((a) => a.code === x.code || !lignes.some((y, j) => j !== i && y.code === a.code))
                        .map((a) => <option key={a.code} value={a.code}>{a.code} — {a.nom}</option>)}
                    </Selecteur>
                  </td>
                  <td style={td}>
                    {/* La technique VIENT du référentiel quand il la connaît ; la
                        saisie libre reste le repli, pour les paramètres qu'il ne
                        compose pas encore. Un champ libre là où une relation
                        existe finit toujours par porter un code qui ne
                        correspond à rien. */}
                    {opts[x.code]?.techniques.length ? (
                      <Selecteur value={x.techniqueCode} aria-label={`Technique de ${x.code}`}
                        onChange={(v) => poser(i, 'techniqueCode', v)}>
                        <option value="">— non précisée —</option>
                        {opts[x.code].techniques.map((tq) => <option key={tq.code} value={tq.code}>{tq.code} — {tq.nom}</option>)}
                      </Selecteur>
                    ) : (
                      <input style={{ ...inputStyle, height: 32, fontSize: 12.5 }} placeholder="technique (facultatif)"
                        aria-label={`Technique de ${x.code || `la ligne ${i + 1}`}`}
                        value={x.techniqueCode} onChange={(e) => poser(i, 'techniqueCode', e.target.value)} />
                    )}
                  </td>
                  <td style={td}>
                    <Selecteur value={x.equipementCode} aria-label={`Équipement de ${x.code || `la ligne ${i + 1}`}`}
                      onChange={(v) => { poser(i, 'equipementCode', v); if (!i) setMiroir(''); }}>
                      <option value="">— choisir —</option>
                      {/* Les appareils que le référentiel rattache à ce paramètre
                          EN PREMIER : sur un parc de trente instruments, c'est la
                          différence entre choisir et chercher. Le reste du parc
                          suit, parce qu'un référentiel incomplet ne doit pas
                          empêcher d'ouvrir un dossier. */}
                      {!!opts[x.code]?.equipements.length && (
                        <optgroup label="Rattachés à ce paramètre">
                          {opts[x.code].equipements.map((e) => <option key={e.code} value={e.code}>{e.code} — {e.nom}</option>)}
                        </optgroup>
                      )}
                      <optgroup label="Tout le parc">
                        {/* Les accessoires sont écartés : ils ne mesurent rien et
                            sont déjà rattachés à l'appareil qu'ils servent. */}
                        {equipementsDesignables(equips).filter((e) => e.code).map((e) => <option key={e.documentId} value={e.code as string}>{e.code} — {e.nom}</option>)}
                      </optgroup>
                    </Selecteur>
                  </td>
                  <td style={{ ...td, textAlign: 'end' }}>
                    <Bouton taille="sm" type="button" title={`Retirer ${x.code || `la ligne ${i + 1}`}`} onClick={() => { setLignes((l) => l.filter((_, j) => j !== i)); setMiroir(''); }}>
                      <I n="x" s={12} />
                    </Bouton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-subtle)' }}>
          <Bouton taille="sm" type="button" onClick={() => setLignes((l) => [...l, { code: '', techniqueCode: '', equipementCode: '' }])}>
            <I n="plus" s={13} />Ajouter un paramètre
          </Bouton>
        </div>
      </div>

      {/* ── LES MATIÈRES BIOLOGIQUES COUVERTES (RQ, 26/08/2026) ────────────
          « Quasiment tout change, bien que le paramètre à chercher soit
          identique. » Le partage tient à un fait physique : où le prélèvement
          PERD sa matière.

          Si elle disparaît avant l'analyse — extraction d'acides nucléiques —
          ce qui entre dans le thermocycleur est de l'ADN en tampon d'élution :
          l'amplification est la même, et un seul dossier suffit. Ce qui reste
          propre à chaque matière (rendement d'extraction, inhibiteurs,
          contamination) se déclare essai par essai, au §5.

          Si elle entre dans l'analyseur — dosage sur liquide de ponction,
          culture cellulaire — c'est un autre examen, et un autre dossier.

          Les matières viennent du catalogue et se décochent. Elles ne se
          saisissent pas : une matière absente de la fiche ferait une ligne de
          portée que le manuel ne promet à personne. */}
      {(examens.find((x) => x.code === examenCode)?.matrices?.length || 0) > 1 && (
        <fieldset style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '12px 14px', margin: 0, display: 'grid', gap: 8 }}>
          <legend style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)', padding: '0 6px' }}>
            Matières biologiques couvertes
          </legend>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Déclarées sur la fiche du catalogue. Décochez celles que ce dossier ne couvre pas —
            chacune est une ligne de portée d’accréditation distincte.
          </p>
          {(examens.find((x) => x.code === examenCode)?.matrices || []).map((m) => (
            <label key={m.code} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, color: 'var(--text-body)' }}>
              <input type="checkbox" checked={matrices.includes(m.code)}
                onChange={(e) => setMatrices((l) => (e.target.checked ? [...l, m.code] : l.filter((x) => x !== m.code)))} />
              <span>
                <strong style={{ color: 'var(--text-strong)' }}>{m.echantillon}</strong>
                {m.recipient ? <span style={{ color: 'var(--text-muted)' }}> · {m.recipient}</span> : null}
                {/* Distinguer ce que le laboratoire a DÉCLARÉ de ce que la
                    plateforme a compris d'une phrase : sur les 8 fiches
                    historiques, la matière n'a ni contenant ni critère propre. */}
                {m.deduite && (
                  <span style={{ fontSize: 12.5, color: 'var(--amber-700)' }}>
                    {' '}— lue dans le champ « échantillon », à déclarer proprement sur la fiche
                  </span>
                )}
              </span>
            </label>
          ))}
          {!matrices.length && (
            <div style={{ fontSize: 12.5, color: 'var(--amber-700)', lineHeight: 1.5 }}>
              Aucune matière cochée : le dossier sera ouvert sans matière déclarée, comme avant le
              26/08. Il ne figurera dans aucune ligne de portée par matière.
            </div>
          )}
        </fieldset>
      )}

      {/* ── DEUX MACHINES POUR LE MÊME PARAMÈTRE ───────────────────────────
          Le cas des deux Indiko, des deux ABL80 (RQ, 25/08/2026). Le §7.3.3
          attend qu'un résultat soit le même quel que soit l'automate qui le
          rend : dès que le référentiel rattache ce paramètre à plus d'un
          appareil, la comparaison est PROPOSÉE.

          Proposée, et pas imposée : le laboratoire peut posséder deux appareils
          sans rendre CET examen sur les deux. Cocher tout seul affirmerait une
          organisation que personne n'a déclarée, sur une pièce
          d'accréditation. */}
      {(optsPremiere?.equipements.length || 0) > 1 && (
        <div style={{ background: 'var(--cyan-50)', borderInlineStart: '3px solid var(--color-primary-active)', borderRadius: 'var(--radius-sm)', padding: '11px 13px', display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.55 }}>
            <strong style={{ color: 'var(--text-strong)' }}>
              {premiere?.code} est rendu par {optsPremiere!.equipements.length} appareils
            </strong>{' '}
            ({optsPremiere!.equipements.map((e) => e.code).join(', ')}).
            Si l’examen est réellement rendu sur plusieurs d’entre eux, désignez l’appareil miroir :
            le dossier ouvrira la <strong>comparaison inter-machines (§5.10)</strong>, qui demande
            30 à 40 mesures appariées. Sans elle, rien ne démontre qu’un patient suivi passe d’un
            automate à l’autre sans rupture.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Appareil miroir :</span>
            <Selecteur value={miroir} onChange={setMiroir} aria-label="Appareil miroir" style={{ minWidth: 240 }}>
              <option value="">— aucun, l’examen n’est rendu que sur un appareil —</option>
              {/* Jamais l'appareil du dossier : se comparer à soi-même ne
                  démontre rien, et le serveur le refuse aussi. */}
              {optsPremiere!.equipements
                .filter((e) => e.code !== premiere?.equipementCode)
                .map((e) => <option key={e.code} value={e.code}>{e.code} — {e.nom}</option>)}
            </Selecteur>
          </div>
        </div>
      )}

      {/* ── CE QUI VAUT POUR TOUT LE DOSSIER ───────────────────────────────
          Le type de résultat et la portée ne se déclinent PAS par paramètre :
          un même dossier ne peut pas être à la fois une vérification et une
          validation. Ils sont donc hors du tableau — et le disent. */}
      <fieldset style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '12px 14px', margin: 0, display: 'grid', gap: 10 }}>
        <legend style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)', padding: '0 6px' }}>
          Pour tout le dossier
        </legend>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
          <div>
            <span style={lbl}>Nature du résultat</span>
            {natureReferentiel ? (
              <>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', padding: '7px 0' }}>
                  {natureReferentiel === 'qualitatif' ? 'Qualitatif' : 'Quantitatif'}
                  {pastille({ label: 'repris du référentiel', bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' })}
                </div>
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Déclarée sur {lignes.length > 1 ? 'les paramètres' : 'le paramètre'} au référentiel.
                  Elle commande les modalités exigées par le gabarit — elle se corrige à l’écran
                  « Référentiel », pas ici.
                </p>
              </>
            ) : (
              <>
                <select aria-label="Nature du résultat" style={inputStyle} value={f.typeResultat}
                  onChange={(e) => setF((x) => ({ ...x, typeResultat: e.target.value }))}>
                  <option value="quantitatif">Quantitatif</option>
                  <option value="qualitatif">Qualitatif</option>
                </select>
                {!!lignes.length && (
                  <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--amber-700)', lineHeight: 1.5 }}>{motifNature}</p>
                )}
              </>
            )}
          </div>
          <div>
            <label style={lbl} htmlFor="dvm-po">Portée</label>
            <select id="dvm-po" style={inputStyle} value={f.portee} onChange={(e) => setF((x) => ({ ...x, portee: e.target.value }))}>
              <option value="A">A — méthode fournisseur adoptée sans modification (vérification)</option>
              <option value="B">B — méthode développée ou modifiée (validation)</option>
            </select>
          </div>
        </div>

        {lignes.length > 1 && (
          <div>
            <label style={lbl} htmlFor="dvm-pres">Présentation du dossier</label>
            <select id="dvm-pres" style={inputStyle} value={presentation}
              onChange={(e) => setPresentation(e.target.value as 'tableau' | 'sections')}>
              <option value="tableau">Tableau — une colonne par paramètre, essais en lignes</option>
              <option value="sections">Sections — le déroulé complet d’un paramètre, puis le suivant</option>
            </select>
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
              Le <strong>tableau</strong> convient à des paramètres qui partagent la même technique et
              se comparent d’un coup d’œil. Les <strong>sections</strong> conviennent à des techniques
              différentes : côte à côte, des colonnes se lisent comme une méthode commune.
              <br />
              Ce choix vaut aussi pour le PDF signé et le Word : une pièce opposable ne peut pas avoir
              deux formes selon qui la regarde. Il reste modifiable tant que le dossier n’est pas clos.
            </p>
          </div>
        )}

        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {typeResultat === 'qualitatif'
            ? '8 modalités seront dépliées : éléments 3, 6, 8, 9, 10, 11, 12, 13.'
            : '11 modalités seront dépliées : éléments 1, 2, 4, 5, 7, 8, 9, 10, 11, 12, 13.'}
          {f.portee === 'B' && ' En portée B, l’étendue de mesure, la robustesse et les intervalles de référence exigeront une étude expérimentale.'}
        </p>
      </fieldset>

      {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: 'Ouvrir le dossier', libelleChargement: 'Ouverture…', chargement: estOccupe('save'), disabled: estOccupe() || !premiere?.code, onClick: () => void creer() }} />
    </div>
  );
}

/* ==========================================================================
 *  Écran
 * ======================================================================== */

export function Dvm() {
  const [d, setD] = useState<Liste | null>(null);
  const [chargement, setChargement] = useState(true);
  const [tour, setTour] = useState(0);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [saisie, setSaisie] = useState(false);

  useEffect(() => {
    if (ouvert) return undefined;
    let vivant = true;
    void fetch('/api/ged/ged/dvm', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setD(j); })
      .catch(() => { if (vivant) setD(null); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [tour, ouvert]);

  const parStatut = useMemo(() => (d?.lignes || []).slice().sort((a, b) => a.ref.localeCompare(b.ref)), [d]);

  if (ouvert) return <Detail id={ouvert} onRetour={() => { setOuvert(null); setTour((t) => t + 1); }} />;
  if (chargement) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement des dossiers…</p>;
  if (!d) return null;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <H1 eyebrow={<>Ressources · <RefISO code="7.3.2" /></>} title="Validation de méthode" />

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Un dossier par examen de biologie médicale, au gabarit <span style={mono}>F.BM.20 rév02</span>.
        L’assistant impose les essais exigés selon le type de résultat et la portée, dit à chaque
        étape ce qui est attendu et pourquoi, et <strong>refuse de conclure « apte » en nommant ce
        qui manque</strong>.
      </p>

      {d.compte.total === 0 && !saisie && (
        <EmptyState
          titre="Aucun dossier de validation de méthode."
          indice="Ce n’est pas « aucune méthode à valider » : c’est qu’aucun dossier n’a encore été ouvert. Un auditeur qui demande le dossier d’un examen accrédité ne trouvera rien."
          action={d.peutEcrire ? <Bouton variante="primaire" onClick={() => setSaisie(true)}><I n="plus" s={14} />Ouvrir le premier dossier</Bouton> : undefined} />
      )}

      {saisie && d.peutEcrire && (
        <Ouvrir onFait={(id) => { setSaisie(false); setOuvert(id); }} onAnnule={() => setSaisie(false)} />
      )}

      {d.compte.total > 0 && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {d.compte.total} dossier{d.compte.total > 1 ? 's' : ''} · {d.compte.brouillons} en saisie ·
              {' '}{d.compte.aApprouver} à approuver · {d.compte.aptes} apte{d.compte.aptes > 1 ? 's' : ''} ·
              {' '}{d.compte.inaptes} inapte{d.compte.inaptes > 1 ? 's' : ''}
            </span>
            <span style={{ flex: 1 }} />
            {d.peutEcrire && !saisie && (
              <Bouton variante="primaire" taille="sm" onClick={() => setSaisie(true)}>
                <I n="plus" s={13} />Ouvrir un dossier
              </Bouton>
            )}
          </div>

          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {parStatut.map((l, i) => {
              const st = STATUTS[l.statut] || STATUTS.brouillon;
              return (
                <button key={l.documentId} onClick={() => setOuvert(l.documentId)}
                  style={{ display: 'block', width: '100%', textAlign: 'start', border: 'none', background: 'transparent', cursor: 'pointer', padding: '11px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ ...mono, fontWeight: 700, color: 'var(--color-primary-active)' }}>{l.ref}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{l.analyteLibelle}</span>
                    {pastille(st)}
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                      {l.mot} · portée {l.portee} · {l.equipementCode || 'équipement non précisé'}
                      {l.version > 1 ? ` · version ${l.version}` : ''}
                    </span>
                    <span style={{ flex: 1 }} />
                    {l.nbBlocages > 0
                      ? <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)' }}>{l.nbBlocages} blocage{l.nbBlocages > 1 ? 's' : ''}</span>
                      : <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--green-600)' }}>complet</span>}
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                      {l.nbEssaisRenseignes} / {l.nbObligatoires} modalités
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
